import React, {useEffect, useMemo, useRef, useState} from 'react';
import {useNavigate, useSearchParams} from 'react-router-dom';
import {onValue, ref, set, update} from 'firebase/database';
import { onValueThrottled } from '../utils/rtdbThrottle';
import {db} from '../firebase';
import {
    TRACKED_APPS as SHARED_TRACKED_APPS,
    MONITORING_PACKAGES,
    UPDATER_PACKAGES,
    PLAYER_PACKAGE,
    isMonitoringPkg,
    findDeviceApp,
    isPackageAllowedForDevices,
} from '../utils/appVersions';
import {
    FiAlertTriangle,
    FiArrowLeft,
    FiCamera,
    FiCheckCircle,
    FiChevronDown,
    FiChevronLeft,
    FiChevronRight,
    FiChevronUp,
    FiEdit3,
    FiEye,
    FiFileText,
    FiMonitor,
    FiPackage,
    FiPlay,
    FiPlus,
    FiPower,
    FiRefreshCw,
    FiRotateCcw,
    FiSearch,
    FiSend,
    FiSettings,
    FiTrash2,
    FiCpu,
    FiUpload,
    FiVolume2,
    FiXCircle
} from 'react-icons/fi';
import {toast, ToastContainer} from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Modal from '../components/Modal';
import {deleteFileFromWasabi, uploadFileToWasabi} from '../utils/wasabiUploader';
import CustomSlider from "../components/CustomSlider";
import CustomDropdown from "../components/CustomDropdown";
import GroupMoveConfirmModal from "../components/GroupMoveConfirmModal";
import {applyGroupChange, planGroupChange, revertGroupChange} from '../utils/groupChanges';

// Group for devices that are out of contract (owner 2026-10-29); must match RETIRED_GROUP in functions-analytics.
const DELETED_GROUP = 'DELETED';
import AppTilePicker from "../components/AppTilePicker";
import {batchUpdate, buildCommandUpdates, buildVolumeUpdates, countDevicesFromPaths} from '../utils/batchOperations';
import {macToSlug} from '../utils/macSlug';
import {countryOfIp} from '../utils/ipCountry';
import { getHwHash, getBoardKey, summarizeBoards, HW_NOT_REPORTED } from '../utils/hwFingerprint';


function formatTimestampHumanely(timestamp) {
    if (!timestamp || isNaN(Number(timestamp))) return 'N/A';

    const date = new Date(Number(timestamp));
    if (isNaN(date.getTime())) return 'Invalid date';

    const pad = (num) => num.toString().padStart(2, '0');

    const day = pad(date.getDate());
    const month = pad(date.getMonth() + 1);
    const year = date.getFullYear();
    const hours = pad(date.getHours());
    const minutes = pad(date.getMinutes());
    const seconds = pad(date.getSeconds());

    return `${day}.${month}.${year} ${hours}:${minutes}:${seconds}`;
}

// Utility to format timestamp
function formatTimestamp(timestamp) {
    return timestamp ? new Date(timestamp).toLocaleString() : 'N/A';
}

// Target apps to track for update status. The monitoring and updater apps each ship under two
// identities (platform line / MDM line) - see utils/appVersions.js; a device carries only one of
// each pair, so lookups go through findDeviceApp(..., MONITORING_PACKAGES) rather than a literal.
const TRACKED_APPS = SHARED_TRACKED_APPS;

export default function DevicesPage() {
    const [devices, setDevices] = useState({});
    const [apps, setApps] = useState({});
    const [otaVersions, setOtaVersions] = useState({}); // OTA versions per device model
    const [selectedDevices, setSelectedDevices] = useState([]);
    const [selectAll, setSelectAll] = useState(false);
    const [groupInput, setGroupInput] = useState('');
    const [updateFilter, setUpdateFilter] = useState(''); // Filter: 'player', 'updater', 'monitoring', 'ota', ''

    // const [searchQuery, setSearchQuery] = useState('');
    // const [selectedGroup, setSelectedGroup] = useState(null);
    // const [selectedStatus, setSelectedStatus] = useState(null);

    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();

    const searchQuery = searchParams.get('q') || '';
    const selectedGroup = searchParams.get('group') || '';
    const selectedStatus = searchParams.get('status') || '';
    const selectedModel = searchParams.get('model') || '';
    const selectedPlayerVersion = searchParams.get('pver') || '';
    // Hardware filters (Monitoring 6.0.4+ hw_fingerprint), both deep-linkable:
    //   board = boardKey (device/SoC/kernel/RAM/eMMC/partition table) - what a firmware
    //           image is compatible with; used by the Hardware dropdown and the Dashboard.
    //   hw    = exact hw_hash, or HW_NOT_REPORTED for boxes without a fingerprint;
    //           used by the device Hardware tab's "Devices with this hash".
    const selectedBoard = searchParams.get('board') || '';
    const selectedHw = searchParams.get('hw') || '';

    const [groupList, setGroupList] = useState({});
    const [ipToCountry, setIpToCountry] = useState({});
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalAction, setModalAction] = useState(null);
    // Bulk group change waiting for confirmation (planGroupChange result), null when no dialog is open.
    const [groupMovePlan, setGroupMovePlan] = useState(null);
    const [modalDeviceId, setModalDeviceId] = useState(null);
    // Uninstall confirmation — uninstalling is destructive (the device
    // loses the app + its data), so we gate the bulk command behind an
    // explicit "yes I'm sure" step instead of firing on first click.
    const [isUninstallConfirmOpen, setIsUninstallConfirmOpen] = useState(false);
    // Free-up-storage confirmation — clears app caches AND deletes /sdcard/Download on every selected
    // device (up to 1000), so we gate it behind an explicit confirm just like the uninstall action.
    const [isFreeStorageConfirmOpen, setIsFreeStorageConfirmOpen] = useState(false);

    // Bulk operation progress state (shown inline in Send Commands modal)
    const [bulkProgress, setBulkProgress] = useState({
        progress: 0,
        totalDevices: 0,
        completedDevices: 0,
        failedDevices: 0,
        status: 'idle',
        operationName: 'Processing'
    });

    // Auto-dismiss the bulk-operation banner once it lands in a terminal
    // state (completed / partial / failed). Users had to hit the X
    // manually before, which left a stale "Operation Completed" toast
    // glued to the screen forever. 4s is long enough to read the
    // success/failure counts but short enough not to be in the way.
    useEffect(() => {
        const terminal = ['completed', 'partial', 'failed'];
        if (!terminal.includes(bulkProgress.status)) return undefined;
        const id = setTimeout(() => {
            setBulkProgress((prev) =>
                terminal.includes(prev.status) ? { ...prev, status: 'idle' } : prev
            );
        }, 4000);
        return () => clearTimeout(id);
    }, [bulkProgress.status]);


    // State for editable fields in the Model tab
    const [editModelData, setEditModelData] = useState({
        device_model: '',
        manufacturer: '',
        uid_android: '',
        hardware_version: '',
    });

    const statusOptions = [
        {value: '', label: 'Any'},
        {value: 'idle', label: 'Idle'},
        {value: 'online', label: 'Online'},
        {value: 'offline', label: 'Offline'},
    ];

    useEffect(() => {
        const devicesRef = ref(db, 'mdm/devices');
        const groupListRef = ref(db, 'mdm/groups');
        const ipToCountryRef = ref(db, 'mdm/ip_to_country');
        const appsRef = ref(db, 'mdm/app_store');
        const otaRef = ref(db, 'mdm/ota');

        const unsubscribeDevices = onValueThrottled(devicesRef, (snapshot) => {
            const data = snapshot.val() || {};
            setDevices(data);
            // console.log("devices", Object.entries(data));
        });

        const unsubscribeGroupList = onValue(groupListRef, (snapshot) => {
            const data = snapshot.val() || {};
            // console.log("group list", snapshot.val())
            setGroupList(data);
        });

        const unsubscribeIpToCountry = onValue(ipToCountryRef, (snapshot) => {
            const data = snapshot.val() || {};
            setIpToCountry(data);
        });

        const unsubscribeApps = onValue(appsRef, (snapshot) => {
            const data = snapshot.val() || {};
            setApps(data);
        });

        const unsubscribeOta = onValue(otaRef, (snapshot) => {
            const data = snapshot.val() || {};
            setOtaVersions(data);
        });

        return () => {
            unsubscribeDevices();
            unsubscribeGroupList();
            unsubscribeIpToCountry();
            unsubscribeApps();
            unsubscribeOta();
        };
    }, []);

    // Countries come from mdm/ip_to_country, filled hourly by the analytics function mdmGeoResolve (DB-IP Lite).
    // The page no longer looks addresses up itself (2026-10-30: it re-asked ip-api.com for every IP on every visit,
    // hit its 45/min limit and wrote "Unknown" over good countries).

    const normalize = str =>
        str.toLowerCase().replace(/[^a-z0-9]/g, '');

// normalize IPs: strip anything that isn’t a digit
    const normalizeIp = ip =>
        (ip || '').replace(/\D/g, '');

    /**
     * Filter devices by searchQuery against MAC, IP, name, customer.
     * Uses substring match on every field so the user can type the
     * last few hex digits of a MAC (e.g. "46:8c") or any fragment of
     * an IP and still find the device.
     * @param {Record<string, any>} devices   keyed by MAC address
     * @param {string} searchQuery
     * @returns {Array<[string, any]>}        [mac, device] entries
     */
    function filterDevices(devices, searchQuery) {
        const q = normalize(searchQuery);
        if (!q) return Object.entries(devices);

        return Object.entries(devices).filter(([mac, device]) => {
            const macNorm = normalize(mac);
            const ipNorm = normalizeIp(device.ip_address);
            const nameNorm = normalize(device.name || '');
            const customerNorm = normalize(device.customer_name || '');
            const licenseNorm = normalize(device.license_code || '');
            const manualNameNorm = normalize(device.manual_device_name || '');
            const manualCustNorm = normalize(device.manual_customer_name || '');
            const firstNorm = normalize(device.first_name || '');
            const lastNorm = normalize(device.last_name || '');
            const businessNorm = normalize(device.business_name || '');

            return macNorm.includes(q) ||
                   ipNorm.includes(q) ||
                   nameNorm.includes(q) ||
                   customerNorm.includes(q) ||
                   licenseNorm.includes(q) ||
                   manualNameNorm.includes(q) ||
                   manualCustNorm.includes(q) ||
                   firstNorm.includes(q) ||
                   lastNorm.includes(q) ||
                   businessNorm.includes(q);
        });
    }

    // Helper: Get installed app version from device's app_list
    const getDeviceAppVersion = (device, packageName) => {
        const appList = device?.app_list || [];
        const app = appList.find(a => a.packageName === packageName);
        // Support both versionName and appVersion field names
        return app?.versionName || app?.appVersion || null;
    };

    // Helper: Get latest app version from app_store (reads latestVersion field)
    const getLatestAppVersion = (packageName) => {
        // Convert package name to safe key format (dots to underscores)
        const safeKey = packageName.replace(/\./g, '_');
        const appData = apps[safeKey];
        if (!appData) return null;
        // Use the explicitly set latestVersion field
        return appData.latestVersion || null;
    };

    // Helper: Compare versions (returns true if installed < latest)
    const needsAppUpdate = (installedVersion, latestVersion) => {
        if (!installedVersion || !latestVersion) return false;
        const installed = installedVersion.split('.').map(Number);
        const latest = latestVersion.split('.').map(Number);
        for (let i = 0; i < Math.max(installed.length, latest.length); i++) {
            const iVal = installed[i] || 0;
            const lVal = latest[i] || 0;
            if (iVal < lVal) return true;
            if (iVal > lVal) return false;
        }
        return false;
    };

    // Helper: Check if device needs OTA update
    const needsOtaUpdate = (device) => {
        const model = device?.device_model;
        const systemVersion = device?.system_version;
        if (!model || !systemVersion) return false;
        const otaData = otaVersions[model];
        if (!otaData?.version) return false;

        // Convert to strings to ensure we can use string methods
        const sysVer = String(systemVersion);
        const otaVer = String(otaData.version);

        // Compare system version with OTA version
        // Extract date from versions like R3.20251225
        const extractDate = (v) => {
            if (!v) return null;
            const str = String(v);
            const match = str.match(/(\d{8})/);
            return match ? match[1] : null;
        };
        const deviceDate = extractDate(sysVer);
        const otaDate = extractDate(otaVer);
        if (deviceDate && otaDate) {
            return otaDate > deviceDate;
        }
        // Fallback to numeric comparison
        try {
            return parseFloat(otaVer) > parseFloat(sysVer);
        } catch {
            return otaVer > sysVer;
        }
    };

    // Helper: Check if device has OTA available (for display, ignores disabled state)
    const hasOtaAvailable = (device) => {
        const model = device?.device_model;
        const systemVersion = device?.system_version;
        if (!model || !systemVersion) return false;
        const otaData = otaVersions[model];
        return otaData?.version ? true : false;
    };

    // Calculate update statistics
    const updateStats = useMemo(() => {
        const devicesList = Object.entries(devices);
        const stats = {
            total: devicesList.length,
            player: { needsUpdate: 0, upToDate: 0, notInstalled: 0, latestVersion: getLatestAppVersion(PLAYER_PACKAGE) },
            // The headline "latest" shown on the card is the platform line's (the fleet majority).
            // The per-device counts below are still compared against each device's OWN line.
            updater: { needsUpdate: 0, upToDate: 0, notInstalled: 0, latestVersion: getLatestAppVersion(UPDATER_PACKAGES[0]) },
            monitoring: { needsUpdate: 0, upToDate: 0, notInstalled: 0, latestVersion: getLatestAppVersion(MONITORING_PACKAGES[0]) },
            ota: { needsUpdate: 0, upToDate: 0 },
            // Player health stats (for online devices only)
            playerHealth: {
                onlineDevices: 0,
                running: 0,
                stopped: 0,
                restarted: 0,
                notInstalled: 0,
                unknown: 0
            },
        };

        devicesList.forEach(([, device]) => {
            // Check Player
            const playerVersion = getDeviceAppVersion(device, PLAYER_PACKAGE);
            if (!playerVersion) {
                stats.player.notInstalled++;
            } else if (needsAppUpdate(playerVersion, stats.player.latestVersion)) {
                stats.player.needsUpdate++;
            } else {
                stats.player.upToDate++;
            }

            // Check Updater - whichever identity this device carries, compared against that
            // identity's own app_store entry (the two lines release independently).
            const updaterApp = findDeviceApp(device, UPDATER_PACKAGES);
            if (!updaterApp) {
                stats.updater.notInstalled++;
            } else if (needsAppUpdate(updaterApp.version, getLatestAppVersion(updaterApp.packageName))) {
                stats.updater.needsUpdate++;
            } else {
                stats.updater.upToDate++;
            }

            // Check Monitoring Service - same rule as the Updater above.
            const monitoringApp = findDeviceApp(device, MONITORING_PACKAGES);
            if (!monitoringApp) {
                stats.monitoring.notInstalled++;
            } else if (needsAppUpdate(monitoringApp.version, getLatestAppVersion(monitoringApp.packageName))) {
                stats.monitoring.needsUpdate++;
            } else {
                stats.monitoring.upToDate++;
            }

            // Check OTA
            if (needsOtaUpdate(device)) {
                stats.ota.needsUpdate++;
            } else {
                stats.ota.upToDate++;
            }

            // Check Player Health (only for online devices)
            if (device.status === 'online') {
                stats.playerHealth.onlineDevices++;
                const playerStatus = device.player_status;
                if (playerStatus === 'running') {
                    stats.playerHealth.running++;
                } else if (playerStatus === 'stopped') {
                    stats.playerHealth.stopped++;
                } else if (playerStatus === 'restarted') {
                    stats.playerHealth.restarted++;
                } else if (playerStatus === 'not_installed') {
                    stats.playerHealth.notInstalled++;
                } else {
                    stats.playerHealth.unknown++;
                }
            }
        });

        return stats;
    }, [devices, apps, otaVersions]);

    const filteredDevices = useMemo(() => {
        let filtered = filterDevices(devices, searchQuery);

        if (selectedGroup === '.nogroup.') {
            filtered = filtered.filter(([, d]) => !d.group);
        } else if (selectedGroup) {
            filtered = filtered.filter(([, d]) => d.group === selectedGroup);
        }

        if (selectedModel) {
            filtered = filtered.filter(([, d]) => d.device_model === decodeURIComponent(selectedModel));
        }

        if (selectedPlayerVersion) {
            const want = decodeURIComponent(selectedPlayerVersion);
            filtered = filtered.filter(([, d]) => getDeviceAppVersion(d, 'rs.musicdj.player') === want);
        }

        if (selectedBoard === HW_NOT_REPORTED) {
            filtered = filtered.filter(([, d]) => !getBoardKey(d));
        } else if (selectedBoard) {
            filtered = filtered.filter(([, d]) => getBoardKey(d) === selectedBoard);
        }

        if (selectedHw === HW_NOT_REPORTED) {
            filtered = filtered.filter(([, d]) => !getHwHash(d));
        } else if (selectedHw) {
            filtered = filtered.filter(([, d]) => getHwHash(d) === selectedHw);
        }

        if (selectedStatus) {
            filtered = filtered.filter(([, d]) => d.status === selectedStatus);
        }

        // Filter by update status
        if (updateFilter) {
            const latestPlayer = getLatestAppVersion(PLAYER_PACKAGE);

            filtered = filtered.filter(([, device]) => {
                if (updateFilter === 'player') {
                    const v = getDeviceAppVersion(device, PLAYER_PACKAGE);
                    return !v || needsAppUpdate(v, latestPlayer);
                }
                // Updater / Monitoring: match the device's own identity against its own store entry.
                if (updateFilter === 'updater') {
                    const a = findDeviceApp(device, UPDATER_PACKAGES);
                    return !a || needsAppUpdate(a.version, getLatestAppVersion(a.packageName));
                }
                if (updateFilter === 'monitoring') {
                    const a = findDeviceApp(device, MONITORING_PACKAGES);
                    return !a || needsAppUpdate(a.version, getLatestAppVersion(a.packageName));
                }
                if (updateFilter === 'ota') {
                    return needsOtaUpdate(device);
                }
                // Player health filters
                if (updateFilter === 'player_running') {
                    return device.status === 'online' && device.player_status === 'running';
                }
                if (updateFilter === 'player_stopped') {
                    return device.status === 'online' && device.player_status === 'stopped';
                }
                if (updateFilter === 'player_restarted') {
                    return device.status === 'online' && device.player_status === 'restarted';
                }
                if (updateFilter === 'player_not_installed') {
                    return device.status === 'online' && device.player_status === 'not_installed';
                }
                if (updateFilter === 'player_not_running') {
                    // All online devices where player is NOT running (stopped, restarted, not_installed, or unknown)
                    return device.status === 'online' && device.player_status !== 'running';
                }
                return true;
            });
        }

        return filtered;
    }, [devices, searchQuery, selectedGroup, selectedModel, selectedPlayerVersion, selectedBoard, selectedHw, selectedStatus, updateFilter, apps, otaVersions]);

    const previousFilteredKeys = useRef([]);

    useEffect(() => {
        const newKeys = filteredDevices.map(([id]) => id);
        if (JSON.stringify(previousFilteredKeys.current) !== JSON.stringify(newKeys)) {
            previousFilteredKeys.current = newKeys;
            setCurrentPage(1);
        }
    }, [filteredDevices]);


    const toggleDeviceSelection = (deviceId) => {
        setSelectedDevices((prev) =>
            prev.includes(deviceId) ? prev.filter((id) => id !== deviceId) : [...prev, deviceId]
        );
    };

    const toggleSelectAll = () => {
        if (selectAll) {
            setSelectedDevices([]);
        } else {
            setSelectedDevices(filteredDevices.map(([deviceId]) => deviceId));
        }
        setSelectAll(!selectAll);
    };

    useEffect(() => {
        setSelectAll(selectedDevices.length === filteredDevices.length);
    }, [selectedDevices, filteredDevices]);

    const closeDeviceCommand = () => {
        setIsSendCommandModalOpen(false)
        setCommandVolumeLevel(50)
        setSelectedInstallApp([])
        setSelectedReinstallApp([])
        setSelectedDeletionApp([])
        setSelectedAppForVersionChange([])
        setSelectedOpenApp([])
        setSelectedRestartApp([])
        setSelectedCloseApp([])
        setSelectedInstallVersion('')
        setSelectedChangingVersion('')
    }

    const toggleDeviceOtaEnabled = async (deviceId) => {
        if (!deviceId) return;

        const currentValue = devices[deviceId]?.ota_enabled;
        // Default to true if not set, so toggling will set to false
        const newValue = currentValue === false ? true : (currentValue === true ? false : false);

        try {
            const deviceRef = ref(db, `mdm/devices/${deviceId}`);
            await update(deviceRef, { ota_enabled: newValue });
            toast.success(`OTA updates ${newValue ? 'enabled' : 'disabled'} for this device`);
        } catch (error) {
            toast.error(`Failed to toggle OTA: ${error.message}`);
        }
    };

    const bulkSetOtaEnabled = async (enabled) => {
        if (selectedDevices.length === 0) {
            toast.error('Select at least one device first');
            return;
        }

        try {
            const updates = {};
            for (const deviceId of selectedDevices) {
                updates[`mdm/devices/${deviceId}/ota_enabled`] = enabled;
            }
            await update(ref(db), updates);
            toast.success(`OTA updates ${enabled ? 'enabled' : 'disabled'} for ${selectedDevices.length} device(s)`);
        } catch (error) {
            toast.error(`Failed to update OTA settings: ${error.message}`);
        }
    };

    // Bulk screen rotation policy (mdm/devices/<MAC>/rotation_lock, applied by Monitoring 6.0.5+). The
    // default is expressed by REMOVING the field (null in a multi-path update), see ControlsTab.
    const bulkSetRotationLock = async (lockLandscape) => {
        if (selectedDevices.length === 0) {
            toast.error('Select at least one device first');
            return;
        }
        try {
            const updates = {};
            for (const deviceId of selectedDevices) {
                updates[`mdm/devices/${deviceId}/rotation_lock`] = lockLandscape ? 'landscape' : null;
            }
            await update(ref(db), updates);
            toast.success(`${lockLandscape ? 'Landscape lock' : 'Player-controlled rotation'} set for ${selectedDevices.length} device(s)`);
        } catch (error) {
            toast.error(`Failed to update rotation policy: ${error.message}`);
        }
    };

    // Bulk kiosk mode (kiosk_mode field, Monitoring 6.0.6+). Always an explicit true/false - the box
    // only reacts to an existing field, so "off" must be written, not removed.
    const bulkSetKioskMode = async (on) => {
        if (selectedDevices.length === 0) {
            toast.error('Select at least one device first');
            return;
        }
        try {
            const updates = {};
            for (const deviceId of selectedDevices) {
                updates[`mdm/devices/${deviceId}/kiosk_mode`] = on;
            }
            await update(ref(db), updates);
            toast.success(`Kiosk mode ${on ? 'on' : 'off'} for ${selectedDevices.length} device(s)`);
        } catch (error) {
            toast.error(`Failed to update kiosk mode: ${error.message}`);
        }
    };

    const sendCommand = async (deviceId, commandObj, setCommandParams, commandParams) => {
        try {
            const userParams = commandParams[commandObj.command] || {};
            const commandPayload = {
                command: commandObj.command,
                ...userParams,
            };

            // Clear feedback field before sending new command
            const feedbackRef = ref(db, `mdm/devices/${deviceId}/feedback`);
            await set(feedbackRef, 'pending');

            const cmdRef = ref(db, `mdm/devices/${deviceId}/command`);
            await set(cmdRef, commandPayload); // Set the command payload

            // Reset the command field in the database to an empty string
            await update(cmdRef, {command: ""});

            toast.success(`Command "${commandObj.command}" sent to device ${deviceId}`);

            // Reset the input fields in the UI
            setCommandParams((prev) => ({
                ...prev,
                [commandObj.command]: {}, // Reset parameters for this specific command
            }));

            const logEntry = {
                command: commandObj.command,
                params: userParams,
                timestamp: Date.now(),
                status: 'success',
            };
            const logRef = ref(db, `mdm/device_logs/${deviceId}/${Date.now()}`);
            await set(logRef, logEntry);

            const deviceRef = ref(db, `mdm/devices/${deviceId}`);
            const updateData = {};
            if (commandObj.command === 'lock_screen') updateData.isLocked = true;
            if (commandObj.command === 'unlock_screen') updateData.isLocked = false;
            if (commandObj.command === 'sleep') updateData.isSleeping = true;
            if (commandObj.command === 'wake_up') updateData.isSleeping = false;
            if (commandObj.command === 'set_volume') updateData.volume_level = userParams.level;
            if (commandObj.command === 'set_brightness') updateData.brightnessLevel = userParams.level;
            if (commandObj.command === 'set_screen_timeout') updateData.screenTimeout = userParams.seconds;
            if (commandObj.command === 'toggle_wifi') updateData.wifiEnabled = userParams.enabled;
            if (commandObj.command === 'toggle_mobile_data') updateData.mobileDataEnabled = userParams.enabled;
            if (commandObj.command === 'start_hotspot') updateData.hotspotEnabled = true;
            if (commandObj.command === 'stop_hotspot') updateData.hotspotEnabled = false;
            if (commandObj.command === 'enable_kiosk_mode') updateData.kiosk_mode = true;
            if (commandObj.command === 'disable_kiosk_mode') updateData.kiosk_mode = false;
            if (commandObj.command === 'block_navigation_buttons') updateData.navigationButtonsBlocked = true;
            if (commandObj.command === 'allow_navigation_buttons') updateData.navigationButtonsBlocked = false;

            if (Object.keys(updateData).length > 0) {
                await update(deviceRef, updateData);
            }
        } catch (error) {
            toast.error(`Failed to send command: ${error.message}`);
            const logEntry = {
                command: commandObj.command,
                params: commandParams[commandObj.command] || {},
                timestamp: Date.now(),
                status: 'failed',
                error: error.message,
            };
            const logRef = ref(db, `mdm/device_logs/${deviceId}/${Date.now()}`);
            await set(logRef, logEntry);
        }
    };

    const sendSingleCommand = async (deviceId, command) => {
        try {
            // Clear feedback field before sending new command
            const feedbackRef = ref(db, `mdm/devices/${deviceId}/feedback`);
            await set(feedbackRef, 'pending');

            const commandPayload = {command};
            const cmdRef = ref(db, `mdm/devices/${deviceId}/command`);
            await set(cmdRef, commandPayload);
            setTimeout(() => set(cmdRef, null), 5000);

            // Update last_seen in the database
            const deviceRef = ref(db, `mdm/devices/${deviceId}`);
            await update(deviceRef, {last_time_seen: Date.now()});

            // Check if the device is live based on the ping timestamp
            const device = devices[deviceId];
            const isLive = device && device.ping && (Date.now() - device?.ping || 0) < 10000;
            toast.success(isLive ? `Device ${deviceId} is live!` : `Device ${deviceId} is not live.`);

            const logEntry = {
                command,
                params: {},
                timestamp: Date.now(),
                status: 'success',
            };
            const logRef = ref(db, `mdm/device_logs/${deviceId}/${Date.now()}`);
            await set(logRef, logEntry);
        } catch (error) {
            toast.error(`Failed to send command: ${error.message}`);
            const logEntry = {
                command,
                params: {},
                timestamp: Date.now(),
                status: 'failed',
                error: error.message,
            };
            const logRef = ref(db, `mdm/device_logs/${deviceId}/${Date.now()}`);
            await set(logRef, logEntry);
        }
    };

    const checkOTAUpdate = async (deviceId, reboot = false) => {
        try {
            // Send OTA update check command
            const otaCommandPayload = {command: 'check_ota_update'};
            const cmdRef = ref(db, `mdm/devices/${deviceId}/command`);
            await set(cmdRef, otaCommandPayload);
            setTimeout(() => set(cmdRef, null), 5000);
            toast.success(`OTA update check initiated for device ${deviceId}`);

            // If reboot is true, send reboot command after OTA update
            if (reboot) {
                const rebootCommandPayload = {command: 'reboot'};
                await set(cmdRef, rebootCommandPayload);
                setTimeout(() => set(cmdRef, null), 5000);
                toast.success(`Reboot command sent to device ${deviceId}`);
            }

            const logEntry = {
                command: reboot ? 'check_ota_update_with_reboot' : 'check_ota_update',
                params: {},
                timestamp: Date.now(),
                status: 'success',
            };
            const logRef = ref(db, `mdm/device_logs/${deviceId}/${Date.now()}`);
            await set(logRef, logEntry);
        } catch (error) {
            toast.error(`Failed to check OTA update: ${error.message}`);
            const logEntry = {
                command: reboot ? 'check_ota_update_with_reboot' : 'check_ota_update',
                params: {},
                timestamp: Date.now(),
                status: 'failed',
                error: error.message,
            };
            const logRef = ref(db, `mdm/device_logs/${deviceId}/${Date.now()}`);
            await set(logRef, logEntry);
        }
    };

    const deleteSelectedDevices = () => {
        if (selectedDevices.length === 0) {
            toast.error('Select at least one device to delete.');
            return;
        }

        setModalAction('bulk');
        setIsModalOpen(true);
    };

    // "Delete" no longer removes the device node: a device that is still switched on reports again and comes
    // back without its history. Owner 2026-10-29: deleted devices move to the DELETED group (out of contract,
    // left out of problem analytics and automatic actions) and keep all their data and controls. Audited and
    // undoable like any group change.
    const confirmDeleteSelectedDevices = async () => {
        try {
            const plan = planGroupChange(devices, selectedDevices, DELETED_GROUP);
            if (plan.moving.length === 0) {
                toast.info('All selected devices are already in DELETED.');
                return;
            }
            const { auditId } = await applyGroupChange(db, plan.moving, DELETED_GROUP, { source: 'devices', action: 'delete' });
            const at = Date.now();
            const logs = {};
            plan.moving.forEach(({ id, from }) => {
                logs[`mdm/device_logs/${id}/${at}`] = {
                    action: 'move_to_deleted', timestamp: at, status: 'success', params: { from: from || '' },
                };
            });
            await update(ref(db), logs);
            const undo = async (toastId) => {
                try {
                    const { restored, skipped } = await revertGroupChange(db, auditId);
                    toast.dismiss(toastId);
                    toast.success(`Undone: ${restored} device(s) back in their previous group` + (skipped ? ` (${skipped} changed again since, left as is)` : ''));
                } catch (e) {
                    toast.error(`Undo failed: ${e.message}`);
                }
            };
            const toastId = toast.success(
                () => (
                    <div className="flex items-center justify-between gap-3">
                        <span>Moved {plan.moving.length} device(s) to DELETED</span>
                        <button
                            onClick={() => undo(toastId)}
                            className="px-2 py-1 rounded-md text-xs font-semibold bg-white/10 hover:bg-white/20 text-white whitespace-nowrap"
                        >
                            Undo
                        </button>
                    </div>
                ),
                { autoClose: 30000, closeOnClick: false }
            );
            setSelectedDevices([]);
            setSelectAll(false);
        } catch (error) {
            toast.error(`Failed to move devices to DELETED: ${error.message}`);
        } finally {
            setIsModalOpen(false);
            setModalAction(null);
        }
    };

    // Step 1 of a bulk group change: never write right away. Work out what would change (devices already in
    // another group, already in the target, without a group) and open the confirmation dialog.
    const assignGroupToSelected = () => {
        if (!groupInput || selectedDevices.length === 0) {
            toast.error('Select a group and at least one device');
            return;
        }
        const target = groupInput === '.nogroup.' ? '' : groupInput;
        const plan = planGroupChange(devices, selectedDevices, target);
        if (plan.moving.length === 0) {
            toast.info(target ? 'All selected devices are already in this group.' : 'None of the selected devices has a group.');
            return;
        }
        setGroupMovePlan(plan);
    };

    // Step 2: the operator confirmed. Write the new groups + the audit entry atomically and offer Undo.
    const confirmGroupMove = async (mode) => {
        const plan = groupMovePlan;
        if (!plan) return;
        const moves = mode === 'ungrouped' ? plan.moving.filter((m) => !m.from) : plan.moving;
        try {
            const { auditId } = await applyGroupChange(db, moves, plan.target, { source: 'devices' });
            const targetName = plan.target ? (groupList[plan.target]?.name || decodeURIComponent(plan.target)) : 'No group';
            const undo = async (toastId) => {
                try {
                    const { restored, skipped } = await revertGroupChange(db, auditId);
                    toast.dismiss(toastId);
                    toast.success(`Undone: ${restored} device(s) back in their previous group` + (skipped ? ` (${skipped} changed again since, left as is)` : ''));
                } catch (e) {
                    toast.error(`Undo failed: ${e.message}`);
                }
            };
            const toastId = toast.success(
                ({ closeToast }) => (
                    <div className="flex items-center justify-between gap-3">
                        <span>{plan.target ? `Moved ${moves.length} device(s) to "${targetName}"` : `Removed ${moves.length} device(s) from their group`}</span>
                        <button
                            onClick={() => undo(toastId)}
                            className="px-2 py-1 rounded-md text-xs font-semibold bg-white/10 hover:bg-white/20 text-white whitespace-nowrap"
                        >
                            Undo
                        </button>
                    </div>
                ),
                { autoClose: 30000, closeOnClick: false }
            );
            setGroupMovePlan(null);
            setGroupInput('');
            setSelectAll(false);
            setSelectedDevices([]);
        } catch (error) {
            console.error('Error during group assignment:', error);
            toast.error(`Failed to change group: ${error.message}`);
        }
    };

    const updateDeviceGroup = async (deviceId, groupName) => {
        try {
            const deviceRef = ref(db, `service/devices/${deviceId}`);
            await update(deviceRef, {group: groupName || null});
            toast.success(`Updated group for device ${deviceId} to ${groupName || 'None'}`);

            // const logEntry = {
            //     action: 'update_group',
            //     group: groupName || 'None',
            //     timestamp: Date.now(),
            //     status: 'success',
            // };
            // const logRef = ref(db, `service/device_logs/${deviceId}/${Date.now()}`);
            // await set(logRef, logEntry);
        } catch (error) {
            toast.error(`Failed to update device group: ${error.message}`);
            // const logEntry = {
            //     action: 'update_group',
            //     group: groupName || 'None',
            //     timestamp: Date.now(),
            //     status: 'failed',
            //     error: error.message,
            // };
            // const logRef = ref(db, `service/device_logs/${deviceId}/${Date.now()}`);
            // await set(logRef, logEntry);
        }
    };

    const getUniqueGroups = () => {
        return Object.entries(groupList)
            .map(([key, group]) => ({ key, name: group.name }))
            .filter(g => g.name); // Optional: ignore groups with missing name
    };

    const getUniqueModels = () => {
        const modelMap = new Map();

        Object.values(devices).forEach(device => {
            const model = device.device_model?.trim();
            if (model) {
                const key = encodeURIComponent(model);
                modelMap.set(key, model); // key ensures uniqueness
            }
        });

        return Array.from(modelMap.entries()).map(([key, model]) => ({ key, model }));
    };

    // Distinct installed Player versions across all devices, newest first. Powers the
    // "Player Version" filter dropdown (same shape as models: {key, version}).
    const getUniquePlayerVersions = () => {
        const set = new Set();
        Object.values(devices).forEach(device => {
            const v = getDeviceAppVersion(device, 'rs.musicdj.player');
            if (v) set.add(v);
        });
        return Array.from(set)
            .sort((a, b) => b.localeCompare(a, undefined, { numeric: true }))
            .map(version => ({ key: encodeURIComponent(version), version }));
    };

    // Hardware BOARDS across the fleet, largest first, plus a "not reported" bucket.
    // Powers the "Hardware" filter dropdown (URL param `board`). The label is the human
    // board description + count, so an operator picks a board, never a hash.
    const hwBoards = useMemo(() => summarizeBoards(devices), [devices]);
    const getHwFilterOptions = () => {
        const opts = hwBoards.boards.map(b => ({
            key: b.key,
            label: `${b.label} (${b.count})`,
        }));
        if (hwBoards.notReported.count > 0) {
            opts.push({ key: HW_NOT_REPORTED, label: `Not reported (${hwBoards.notReported.count})` });
        }
        return opts;
    };

    const [currentPage, setCurrentPage] = useState(1);
    const [devicesPerPage, setDevicesPerPage] = useState(50); // Default to 50 devices per page
    const [showMacAddress, setShowMacAddress] = useState(false); // MAC address hidden by default
    const [showIpAddress, setShowIpAddress] = useState(false); // IP address hidden by default
    const [showLastSeen, setShowLastSeen] = useState(false); // Last seen hidden by default
    const [showFeedback, setShowFeedback] = useState(false); // Feedback hidden by default
    const [showOta, setShowOta] = useState(false); // OTA column hidden by default
    const [showPlayerVersion, setShowPlayerVersion] = useState(false); // Player version hidden by default
    const [showSystem, setShowSystem] = useState(false); // System column hidden by default
    const [showMsVer, setShowMsVer] = useState(false); // MS Ver column hidden by default
    const [showModel, setShowModel] = useState(false); // Model column hidden by default
    const [showCountry, setShowCountry] = useState(false); // Country column hidden by default
    const [showHardware, setShowHardware] = useState(false); // HW (hw_hash) column hidden by default
    const [showKiosk, setShowKiosk] = useState(false); // Kiosk column hidden by default

    const sortedDevices = useMemo(() => {
        return filteredDevices
            .map(([deviceId, info]) => ({
                key: deviceId,
                name: info.name ?? '', // fallback if missing
                ...info,
            }))
            .sort((a, b) => a.name.localeCompare(b.name));
    }, [filteredDevices]);

    const paginatedDeviceKeys = useMemo(() => {
        const pages = [];
        for (let i = 0; i < sortedDevices.length; i += devicesPerPage) {
            const pageSlice = sortedDevices.slice(i, i + devicesPerPage);
            pages.push(pageSlice.map(device => device.key));
        }
        return pages;
    }, [sortedDevices]);

    const totalPages = paginatedDeviceKeys.length;

    const currentDeviceKeys = paginatedDeviceKeys[currentPage - 1] || [];

    const currentDevices = sortedDevices.filter(device =>
        currentDeviceKeys.includes(device.key)
    );

    const paginate = (pageNumber) => {
        if (pageNumber >= 1 && pageNumber <= totalPages) {
            setCurrentPage(pageNumber);
        }
    };

    const [isSendCommandModalOpen, setIsSendCommandModalOpen] = useState(false);
    // Send Commands modal — left-rail category navigation. Each section
    // ('system' / 'audio' / 'apps' / 'diagnostics' / 'transfer') maps
    // to one of the existing <details> blocks; inactive sections are
    // hidden via display:none rather than unmounting so JSX structure
    // stays untouched.
    const [activeCommandCategory, setActiveCommandCategory] = useState('system');

    const [commandVolumeLevel, setCommandVolumeLevel] = useState(50);

    const [sharedCommands, setSharedCommands] = useState([]);
    const [kioskModeEnabled, setKioskModeEnabled] = useState(false);
    const [selectedApp, setSelectedApp] = useState('');
    const [selectedAppVersion, setSelectedAppVersion] = useState('');

    const allPossibleCommands = ["allowInstall", "allowReinstall", "allowDelete", "allowVersionChange", "allowOpen", "allowRestart", "allowClose", "allowReboot", "allowReset", "kioskMode"]

// These will be used for app dropdowns (delete/reinstall/open/close/restart)
    const [commonApps, setCommonApps] = useState([]);

    const [selectedInstallApp, setSelectedInstallApp] = useState([]);
    const [selectedReinstallApp, setSelectedReinstallApp] = useState([]);
    const [selectedDeletionApp, setSelectedDeletionApp] = useState([]);
    const [selectedAppForVersionChange, setSelectedAppForVersionChange] = useState([]);

    const [selectedOpenApp, setSelectedOpenApp] = useState([]);
    const [selectedRestartApp, setSelectedRestartApp] = useState([]);
    const [selectedCloseApp, setSelectedCloseApp] = useState([]);


    const [selectedInstallVersion, setSelectedInstallVersion] = useState("");
    const [selectedChangingVersion, setSelectedChangingVersion] = useState("");

    // File transfer state
    const [uploadFile, setUploadFile] = useState(null);
    const [uploadDestPath, setUploadDestPath] = useState('/sdcard/Download/');
    const [isUploading, setIsUploading] = useState(false);
    const fileInputRef = useRef(null);

    const selectedAppVersions = useMemo(() => {
        const app = apps[selectedInstallApp];
        if (!app) return [];

        // Use versionCode (index/key) as identifier - works for all apps
        if (Array.isArray(app.versions)) {
            return app.versions
                .map((v, index) => (v != null) ? String(index) : null)
                .filter(v => v != null);
        }
        if (typeof app.versions === "object" && app.versions !== null) {
            return Object.keys(app.versions);
        }

        return [];
    }, [selectedInstallApp, apps]);

    const singleVersion = selectedAppVersions.length === 1;

    useEffect(() => {
        if (singleVersion) {
            setSelectedInstallVersion(selectedAppVersions[0]);
        }
    }, [selectedAppVersions]);

    const selectedAppVersionsForChange = useMemo(() => {
        const app = Object.values(apps).find(app => app.originalPackageName === selectedAppForVersionChange);
        if (!app) return [];

        if (Array.isArray(app.versions)) {
            return app.versions
                .map((v, index) => (v != null) ? String(index) : null)
                .filter(v => v != null);
        }
        if (typeof app.versions === "object" && app.versions !== null) {
            return Object.keys(app.versions);
        }

        return [];
    }, [selectedAppForVersionChange, apps]);

    const singleVersionForChange = selectedAppVersionsForChange.length === 1;

    useEffect(() => {
        if (singleVersionForChange) {
            setSelectedChangingVersion(selectedAppVersionsForChange[0]);
        }
    }, [selectedAppVersionsForChange]);

    useEffect(() => {
        if (!selectedDevices.length) return;

        // Identify groups of selected devices (excluding devices without group)
        const [groupedDeviceIds, ungroupedDeviceIds] = selectedDevices.reduce(
            ([grouped, ungrouped], id) => {
                const hasGroup = !!devices[id]?.group;
                if (hasGroup) grouped.push(id);
                else ungrouped.push(id);
                return [grouped, ungrouped];
            },
            [[], []]
        );

        if (groupedDeviceIds.length === 0) {
            // All devices are ungrouped → full command list
            setSharedCommands(allPossibleCommands);
        } else {
            // Get group settings for groups used
            const groupIds = Array.from(new Set(groupedDeviceIds.map(id => devices[id]?.group)));
            const groupSettingsArray = groupIds.map(id => groupList[id]?.settings || {}); // assume groupsData has a `settings` field

            // Determine commands that are true in *all* group settings
            const allSettingKeys = Object.keys(groupSettingsArray[0] || {}); // assume all groups have same keys

            const shared = allSettingKeys.filter(key =>
                groupSettingsArray.every(settings => settings[key] === true)
            );

            setSharedCommands(shared);
        }

        // Common apps (across all devices)
        // const packageNameSets = selectedDevices.map(id => {
        //     const appList = devices[id]?.app_list || [];
        //     return new Set(appList.map(app => app.packageName));
        // });
        //
        // const commonPackageNames =
        //     packageNameSets.length > 0
        //         ? [...packageNameSets[0]].filter(pkg =>
        //             packageNameSets.every(set => set.has(pkg))
        //         )
        //         : [];
        // setCommonApps(commonPackageNames);
        const packageNameSets = selectedDevices.map(id => {
            const appList = devices[id]?.app_list || [];
            return new Set(appList.map(app => app.packageName));
        });

        // console.log("packageName set", packageNameSets)

        const commonPackageNames =
            packageNameSets.length > 0
                ? [...packageNameSets[0]].filter(pkg =>
                    packageNameSets.every(set => set.has(pkg))
                )
                : [];

        // console.log("commonPackageNames", commonPackageNames)

        const commonAppsWithNames = commonPackageNames.map(packageName => {
            // Find first app object that matches the packageName
            for (const id of selectedDevices) {
                const appList = devices[id]?.app_list || [];
                const match = appList.find(app => app.packageName === packageName);
                if (match) {

                    return {
                        packageName,
                        appName: match.appName || packageName, // fallback if name is missing
                    };
                }
            }
            return { packageName, appName: packageName }; // Fallback
        });
        setCommonApps(commonAppsWithNames);
        // console.log("common apps", commonApps)

    }, [selectedDevices, devices, groupList]);

    // Apps offered for installation on the current selection. The store holds the agent for BOTH
    // product lines, and pushing the wrong one at a device gives it an APK it can never run (other
    // signing key; on Android 12 and older the sharedUserId even makes the install fail) plus a
    // second agent fighting the real one. So the agent of a line is offered only when every selected
    // device is on that line; the Player and third-party apps are always offered. With nothing
    // selected, or devices whose line we cannot read yet, nothing is filtered out.
    const installableApps = useMemo(() => {
        const targets = selectedDevices.map((id) => devices[id]).filter(Boolean);
        return Object.entries(apps).filter(([, app]) =>
            isPackageAllowedForDevices(app?.originalPackageName, targets));
    }, [apps, selectedDevices, devices]);

    // Delete screenshot or log from Wasabi and clear Firebase reference
    const handleDeleteDiagnosticFile = async (deviceId, fileType) => {
        try {
            const device = devices[deviceId];
            if (!device) {
                toast.error('Device not found');
                return;
            }

            const fileUrl = fileType === 'screenshot' ? device.last_screenshot : device.last_log;
            if (!fileUrl) {
                toast.error(`No ${fileType} to delete`);
                return;
            }

            // Delete from Wasabi
            await deleteFileFromWasabi(fileUrl);

            // Clear Firebase reference
            const updates = {};
            if (fileType === 'screenshot') {
                updates[`mdm/devices/${deviceId}/last_screenshot`] = null;
                updates[`mdm/devices/${deviceId}/last_screenshot_time`] = null;
            } else {
                updates[`mdm/devices/${deviceId}/last_log`] = null;
                updates[`mdm/devices/${deviceId}/last_log_time`] = null;
            }
            await update(ref(db), updates);

            toast.success(`${fileType === 'screenshot' ? 'Screenshot' : 'Log'} deleted successfully`);
        } catch (error) {
            toast.error(`Failed to delete ${fileType}: ${error.message}`);
        }
    };

    const handleSendCommand = async ({value, successMsg = 'Update successful.', errorMsg = 'Update failed.'}) => {
        if (!selectedDevices?.length) {
            toast.error('Select at least one device');
            return;
        }

        const totalDevices = selectedDevices.length;
        const updates = buildCommandUpdates(selectedDevices, value, true);

        // Always show inline progress for group commands (more than 1 device)
        const showProgress = totalDevices > 1;

        if (showProgress) {
            setBulkProgress({
                progress: 0,
                totalDevices,
                completedDevices: 0,
                failedDevices: 0,
                status: 'running',
                operationName: 'Sending Commands'
            });
        }

        try {
            let completedDevices = 0;
            let failedDevices = 0;

            const results = await batchUpdate(db, updates, {
                onProgress: (progress) => {
                    if (showProgress) {
                        setBulkProgress(prev => ({ ...prev, progress }));
                    }
                },
                onBatchComplete: ({ batch, totalBatches, success, pathsInBatch }) => {
                    // Each device has 2 paths (command + feedback)
                    const devicesInBatch = Math.ceil(pathsInBatch / 2);
                    if (success) {
                        completedDevices += devicesInBatch;
                    } else {
                        failedDevices += devicesInBatch;
                    }
                    if (showProgress) {
                        setBulkProgress(prev => ({
                            ...prev,
                            completedDevices,
                            failedDevices
                        }));
                    }
                }
            });

            const successCount = countDevicesFromPaths(results.success);
            const failCount = countDevicesFromPaths(results.failed);

            if (showProgress) {
                setBulkProgress(prev => ({
                    ...prev,
                    progress: 100,
                    completedDevices: successCount,
                    failedDevices: failCount,
                    status: failCount > 0 ? 'partial' : 'completed'
                }));
            }

            if (failCount === 0) {
                if (!showProgress) toast.success(successMsg);
            } else if (successCount > 0) {
                toast.warning(`Command sent to ${successCount} devices, ${failCount} failed`);
            } else {
                toast.error(errorMsg);
            }
        } catch (error) {
            if (showProgress) {
                setBulkProgress(prev => ({ ...prev, status: 'failed' }));
            }
            toast.error(`${errorMsg}: ${error.message}`);
        }
    }

    // Handle file upload and send download command to devices
    const handleFileUpload = async () => {
        if (!uploadFile) {
            toast.error('Please select a file to upload');
            return;
        }
        if (!selectedDevices?.length) {
            toast.error('Select at least one device');
            return;
        }
        if (!uploadDestPath.trim()) {
            toast.error('Please enter a destination path');
            return;
        }

        setIsUploading(true);
        const totalDevices = selectedDevices.length;

        try {
            // Step 1: Upload file to Wasabi TMP_UPLOAD folder
            setBulkProgress({
                progress: 0,
                totalDevices,
                completedDevices: 0,
                failedDevices: 0,
                status: 'running',
                operationName: 'Uploading File'
            });

            const { publicUrl, fileName } = await uploadFileToWasabi(uploadFile, 'TMP_UPLOAD');

            setBulkProgress(prev => ({
                ...prev,
                progress: 30,
                operationName: 'Sending Download Commands'
            }));

            // Step 2: Build destination path (ensure it ends with filename)
            let destPath = uploadDestPath.trim();
            if (destPath.endsWith('/')) {
                destPath = destPath + fileName;
            } else if (!destPath.includes('.')) {
                // If path doesn't have extension, append filename
                destPath = destPath + '/' + fileName;
            }

            // Step 3: Send download command to all selected devices
            const downloadCommand = `download:${publicUrl}:${destPath}`;
            const updates = buildCommandUpdates(selectedDevices, downloadCommand, true);

            let completedDevices = 0;
            let failedDevices = 0;

            const results = await batchUpdate(db, updates, {
                onProgress: (progress) => {
                    // Scale progress from 30% to 100%
                    setBulkProgress(prev => ({ ...prev, progress: 30 + (progress * 0.7) }));
                },
                onBatchComplete: ({ success, pathsInBatch }) => {
                    const devicesInBatch = Math.ceil(pathsInBatch / 2);
                    if (success) {
                        completedDevices += devicesInBatch;
                    } else {
                        failedDevices += devicesInBatch;
                    }
                    setBulkProgress(prev => ({
                        ...prev,
                        completedDevices,
                        failedDevices
                    }));
                }
            });

            const successCount = countDevicesFromPaths(results.success);
            const failCount = countDevicesFromPaths(results.failed);

            setBulkProgress(prev => ({
                ...prev,
                progress: 100,
                completedDevices: successCount,
                failedDevices: failCount,
                status: failCount > 0 ? 'partial' : 'completed'
            }));

            if (failCount === 0) {
                toast.success(`File transfer initiated to ${successCount} device(s)`);
            } else if (successCount > 0) {
                toast.warning(`File transfer initiated to ${successCount} devices, ${failCount} failed`);
            } else {
                toast.error('File transfer failed');
            }

            // Reset file input
            setUploadFile(null);
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }

        } catch (error) {
            setBulkProgress(prev => ({ ...prev, status: 'failed' }));
            toast.error(`File upload failed: ${error.message}`);
        } finally {
            setIsUploading(false);
        }
    };

    const handleSetVolume = async () => {
        if (selectedDevices.length === 0) {
            toast.error('Select at least one device');
            return;
        }

        const totalDevices = selectedDevices.length;
        const updates = buildVolumeUpdates(selectedDevices, commandVolumeLevel);

        // Always show inline progress for group operations (more than 1 device)
        const showProgress = totalDevices > 1;

        if (showProgress) {
            setBulkProgress({
                progress: 0,
                totalDevices,
                completedDevices: 0,
                failedDevices: 0,
                status: 'running',
                operationName: 'Setting Volume'
            });
        }

        try {
            let completedDevices = 0;
            let failedDevices = 0;

            const results = await batchUpdate(db, updates, {
                onProgress: (progress) => {
                    if (showProgress) {
                        setBulkProgress(prev => ({ ...prev, progress }));
                    }
                },
                onBatchComplete: ({ success, pathsInBatch }) => {
                    if (success) {
                        completedDevices += pathsInBatch;
                    } else {
                        failedDevices += pathsInBatch;
                    }
                    if (showProgress) {
                        setBulkProgress(prev => ({
                            ...prev,
                            completedDevices,
                            failedDevices
                        }));
                    }
                }
            });

            const successCount = countDevicesFromPaths(results.success);
            const failCount = countDevicesFromPaths(results.failed);

            if (showProgress) {
                setBulkProgress(prev => ({
                    ...prev,
                    progress: 100,
                    completedDevices: successCount,
                    failedDevices: failCount,
                    status: failCount > 0 ? 'partial' : 'completed'
                }));
            }

            if (failCount === 0) {
                if (!showProgress) toast.success(`Changed volume for ${successCount} device${successCount !== 1 ? 's' : ''} to ${commandVolumeLevel}%`);
            } else if (successCount > 0) {
                toast.warning(`Volume set for ${successCount} devices, ${failCount} failed`);
            } else {
                toast.error('Failed to set volume');
            }
        } catch (error) {
            if (showProgress) {
                setBulkProgress(prev => ({ ...prev, status: 'failed' }));
            }
            console.error('Error during setting volume:', error);
            toast.error(`Failed to set volume: ${error.message}`);
        }
    }

    return (
        <div className="page-shell">
            <ToastContainer
                position="top-right"
                autoClose={3000}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme="dark"
            />

            <GroupMoveConfirmModal
                isOpen={!!groupMovePlan}
                plan={groupMovePlan}
                devices={devices}
                groupList={groupList}
                totalDevices={Object.keys(devices).length}
                onClose={() => setGroupMovePlan(null)}
                onConfirm={confirmGroupMove}
            />

            <Modal
                isOpen={isModalOpen}
                onClose={() => {
                    setIsModalOpen(false);
                    setModalAction(null);
                    setModalDeviceId(null);
                }}
                onConfirm={confirmDeleteSelectedDevices}
                title="Move to DELETED"
                message={`Move ${selectedDevices.length} selected device(s) to the DELETED group? They keep all their data and can still be controlled, but are left out of problem analytics and automatic actions. You can undo it right after, or later from the group change history.`}
                confirmText="Move to DELETED"
                cancelText="Cancel"
            />

            {/* Uninstall confirmation — fired from the Send Commands
                modal's Delete App tile picker. The Send Commands modal
                itself sits at z-50, so this confirm needs a higher
                z-index to land on top. Resolves the human-readable app
                name from `commonApps` so the message reads naturally
                instead of showing a raw package id. */}
            <Modal
                isOpen={isUninstallConfirmOpen}
                onClose={() => setIsUninstallConfirmOpen(false)}
                onConfirm={() => {
                    setIsUninstallConfirmOpen(false);
                    handleSendCommand({value:`uninstall:${selectedDeletionApp}`, successMsg:'App uninstallation command sending successful', errorMsg:'App uninstallation failed'});
                }}
                title="Uninstall App"
                message={`Are you sure you want to uninstall "${commonApps.find((a) => a.packageName === selectedDeletionApp)?.appName || selectedDeletionApp}" from ${selectedDevices.length} selected device(s)?`}
                confirmText="Uninstall"
                cancelText="Cancel"
                zIndexClass="z-[60]"
            />

            {/* Free-up-storage confirmation — fired from the Send Commands modal's System
                Control section. z-[60] so it lands on top of the (z-50) Send Commands modal. */}
            <Modal
                isOpen={isFreeStorageConfirmOpen}
                onClose={() => setIsFreeStorageConfirmOpen(false)}
                onConfirm={() => {
                    setIsFreeStorageConfirmOpen(false);
                    handleSendCommand({value:'free_space', successMsg:'Free-up-storage command sent (clears caches + Downloads)', errorMsg:'Free-up-storage failed'});
                }}
                title="Free Up Storage"
                message={`This evicts all app caches AND deletes everything in /sdcard/Download on ${selectedDevices.length} selected device(s). App caches rebuild automatically; Downloads only holds transient files. Continue?`}
                confirmText="Free Up Storage"
                cancelText="Cancel"
                zIndexClass="z-[60]"
            />


                    {isSendCommandModalOpen && (
                        <div
                            className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
                            <div
                                className="scroll-brand text-white rounded-lg shadow-lg max-h-[90vh] overflow-y-auto w-full max-w-6xl">
                                <div
                                    className="card-chrome">
                                    <div className="flex items-center justify-between mb-4">
                                        <h2 className="text-3xl font-bold flex items-center gap-2">
                                            <FiSend className="text-brand-400"/> Send Commands
                                        </h2>
                                        <button
                                            onClick={closeDeviceCommand}
                                            className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-red-500/15 border border-red-500/40 text-red-300 hover:bg-red-500/25 transition-colors"
                                        >
                                            <FiArrowLeft/> Close
                                        </button>
                                    </div>

                                            {/* Inline Progress/Status Bar */}
                                            {bulkProgress.status !== 'idle' && (
                                                <div className={`mb-4 p-3 rounded-lg border ${
                                                    bulkProgress.status === 'running' ? 'bg-blue-900/30 border-blue-700' :
                                                    bulkProgress.status === 'completed' ? 'bg-green-900/30 border-green-700' :
                                                    bulkProgress.status === 'partial' ? 'bg-yellow-900/30 border-yellow-700' :
                                                    'bg-red-900/30 border-red-700'
                                                }`}>
                                                    <div className="flex items-center justify-between mb-2">
                                                        <div className="flex items-center gap-2">
                                                            {bulkProgress.status === 'running' && (
                                                                <FiRefreshCw className="w-4 h-4 text-brand-400 animate-spin" />
                                                            )}
                                                            {bulkProgress.status === 'completed' && (
                                                                <FiCheckCircle className="w-4 h-4 text-green-400" />
                                                            )}
                                                            {bulkProgress.status === 'partial' && (
                                                                <FiAlertTriangle className="w-4 h-4 text-yellow-400" />
                                                            )}
                                                            {bulkProgress.status === 'failed' && (
                                                                <FiXCircle className="w-4 h-4 text-red-400" />
                                                            )}
                                                            <span className={`text-sm font-medium ${
                                                                bulkProgress.status === 'running' ? 'text-blue-300' :
                                                                bulkProgress.status === 'completed' ? 'text-green-300' :
                                                                bulkProgress.status === 'partial' ? 'text-yellow-300' :
                                                                'text-red-300'
                                                            }`}>
                                                                {bulkProgress.status === 'running' ? `${bulkProgress.operationName}...` :
                                                                 bulkProgress.status === 'completed' ? 'Operation Completed Successfully' :
                                                                 bulkProgress.status === 'partial' ? 'Completed with Errors' :
                                                                 'Operation Failed'}
                                                            </span>
                                                        </div>
                                                        <div className="flex items-center gap-3 text-xs">
                                                            <span className="text-green-400">{bulkProgress.completedDevices} success</span>
                                                            {bulkProgress.failedDevices > 0 && (
                                                                <span className="text-red-400">{bulkProgress.failedDevices} failed</span>
                                                            )}
                                                            <span className="text-gray-400">/ {bulkProgress.totalDevices} total</span>
                                                            {bulkProgress.status !== 'running' && (
                                                                <button
                                                                    onClick={() => setBulkProgress(prev => ({ ...prev, status: 'idle' }))}
                                                                    className="text-gray-400 hover:text-white ml-2"
                                                                    title="Dismiss"
                                                                >
                                                                    <FiXCircle className="w-4 h-4" />
                                                                </button>
                                                            )}
                                                        </div>
                                                    </div>
                                                    {/* Progress bar */}
                                                    <div className="w-full h-1.5 bg-white/[0.06] rounded-full overflow-hidden">
                                                        <div
                                                            className={`h-full transition-all duration-300 ${
                                                                bulkProgress.status === 'running' ? 'bg-blue-500' :
                                                                bulkProgress.status === 'completed' ? 'bg-green-500' :
                                                                bulkProgress.status === 'partial' ? 'bg-yellow-500' :
                                                                'bg-red-500'
                                                            }`}
                                                            style={{ width: `${bulkProgress.progress}%` }}
                                                        />
                                                    </div>
                                                </div>
                                            )}

                                            <h2 className="text-xl font-semibold mb-6 border-b border-gray-700 pb-2">
                                                Send Commands to {selectedDevices.length} Selected Device{selectedDevices.length !== 1 ? 's' : ''}
                                            </h2>

                                            <div className="grid grid-cols-1 md:grid-cols-[200px_1fr] gap-5">
                                                {/* Sidebar — category nav. Buttons toggle which
                                                    section's <details> is visible below. The other
                                                    sections stay mounted (preserving any input state)
                                                    but get display:none. */}
                                                <aside className="md:border-r md:border-white/[0.06] md:pr-3 flex md:flex-col gap-1 overflow-x-auto md:overflow-x-visible">
                                                    {[
                                                        { id: 'system',      label: 'System Control', icon: <FiSettings /> },
                                                        { id: 'audio',       label: 'Audio',          icon: <FiVolume2 /> },
                                                        { id: 'apps',        label: 'Apps',           icon: <FiPackage /> },
                                                        { id: 'diagnostics', label: 'Diagnostics',    icon: <FiCamera /> },
                                                        { id: 'transfer',    label: 'File Transfer',  icon: <FiUpload /> },
                                                    ].map(({ id, label, icon }) => {
                                                        const active = activeCommandCategory === id;
                                                        return (
                                                            <button
                                                                key={id}
                                                                type="button"
                                                                onClick={() => setActiveCommandCategory(id)}
                                                                className={`w-full inline-flex items-center gap-2.5 px-3 py-2 rounded-md text-sm font-medium whitespace-nowrap transition-colors ${
                                                                    active
                                                                        ? 'bg-brand/15 text-white'
                                                                        : 'text-gray-400 hover:text-white hover:bg-white/[0.04]'
                                                                }`}
                                                            >
                                                                <span className={active ? 'text-brand-400' : 'text-gray-500'}>{icon}</span>
                                                                {label}
                                                            </button>
                                                        );
                                                    })}
                                                </aside>

                                                {/* Content — only the section matching the active
                                                    sidebar tab is visible (others hidden via style). */}
                                                <section className="space-y-4">
                                                    {/* 1 — System Control */}
                                                    <details
                                                        open
                                                        className="card-inset p-4 group"
                                                        style={{ display: activeCommandCategory === 'system' ? 'block' : 'none' }}
                                                    >
                                                        <summary
                                                            className="flex items-center justify-between cursor-pointer select-none text-white">
                                                            {/* Left Side: Icon + Label */}
                                                            <span className="flex items-center gap-2 font-medium">
                                            <FiSettings className="text-brand-400"/>System&nbsp;Control</span>

                                                            {/* Right Side: Status + Chevron together */}
                                                            <span className="flex items-center gap-3">
                                            <FiChevronDown
                                                className="transition-transform duration-200 group-open:rotate-180"/>
                                        </span>
                                                        </summary>
                                                        <div className="w-full mt-4">
                                                            {/* Grouped by function, two groups per row. Each group = tiny uppercase label + its
                                                                buttons, so related actions (On/Off, lock/unlock) read as one unit instead of a
                                                                single long ribbon of unrelated buttons. Groups gated by sharedCommands simply
                                                                drop out of the grid. */}
                                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">

                                                                {/* Power */}
                                                                {(sharedCommands.includes('allowReboot') || sharedCommands.includes('allowReset')) && (
                                                                    <div className="rounded-lg border border-white/[0.06] p-3">
                                                                        <div className="text-[11px] uppercase tracking-wider text-gray-500 mb-2 inline-flex items-center gap-1.5">
                                                                            <FiPower /> Power
                                                                        </div>
                                                                        <div className="flex flex-wrap gap-2">
                                                                            {sharedCommands.includes('allowReboot') && (
                                                                                <button
                                                                                    onClick={() => handleSendCommand({value:'reboot', successMsg:'Reboot command sending successful', errorMsg:'Reboot failed'})}
                                                                                    className="btn-secondary"
                                                                                >
                                                                                    Reboot Devices
                                                                                </button>
                                                                            )}
                                                                            {sharedCommands.includes('allowReset') && (
                                                                                <button
                                                                                    onClick={() => handleSendCommand({value:'factory_reset', successMsg:'Factory reset command sending successful', errorMsg:'Factory reset failed'})}
                                                                                    className="btn-secondary"
                                                                                >
                                                                                    Factory Reset
                                                                                </button>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                )}

                                                                {/* Kiosk mode - kiosk_mode field, Monitoring 6.0.6+. The state of the SELECTED devices is shown on
                                                                    the buttons (green = kiosk on for all selected, highlighted Off = off for all, neutral when
                                                                    mixed), plus what the boxes report back in kiosk_state. */}
                                                                {sharedCommands.includes('kioskMode') && (() => {
                                                                    const selected = selectedDevices.map(id => devices[id]).filter(Boolean);
                                                                    const total = selected.length;
                                                                    const on = selected.filter(d => d?.kiosk_mode === true).length;
                                                                    const allOn = total > 0 && on === total;
                                                                    const allOff = total > 0 && on === 0;
                                                                    const states = selected.map(d => d?.kiosk_state).filter(Boolean);
                                                                    const failed = states.filter(st => st === 'failed').length;
                                                                    const stateSummary = states.length === 0
                                                                        ? 'box state not reported yet'
                                                                        : failed > 0
                                                                            ? `${failed} box(es) failed to apply`
                                                                            : `box reports ${states.filter(st => st === 'on').length} on / ${states.filter(st => st === 'off').length} off`;
                                                                    const activeOn = 'bg-green-500/15 border-green-500/40 text-green-300 hover:bg-green-500/25';
                                                                    const activeOff = 'bg-white/[0.10] border-white/30 text-white';
                                                                    return (
                                                                        <div className="rounded-lg border border-white/[0.06] p-3">
                                                                            <div className="text-[11px] uppercase tracking-wider text-gray-500 mb-2 inline-flex items-center gap-1.5">
                                                                                <FiMonitor /> Kiosk Mode
                                                                            </div>
                                                                            <div className="flex flex-wrap gap-2">
                                                                                <button
                                                                                    onClick={() => bulkSetKioskMode(true)}
                                                                                    aria-pressed={allOn}
                                                                                    className={`btn-secondary ${allOn ? activeOn : ''}`}
                                                                                    title="HOME and BACK always return to the player (Monitoring 6.0.6+)"
                                                                                >
                                                                                    Kiosk On
                                                                                </button>
                                                                                <button
                                                                                    onClick={() => bulkSetKioskMode(false)}
                                                                                    aria-pressed={allOff}
                                                                                    className={`btn-secondary ${allOff ? activeOff : ''}`}
                                                                                    title="Restore the normal launcher"
                                                                                >
                                                                                    Kiosk Off
                                                                                </button>
                                                                            </div>
                                                                            <div className={`text-[11px] mt-2 ${failed > 0 ? 'text-red-400' : 'text-gray-500'}`}>
                                                                                {total === 0
                                                                                    ? 'Select devices to see their kiosk mode'
                                                                                    : allOn
                                                                                        ? `Active: Kiosk on${total > 1 ? ` on all ${total}` : ''} · ${stateSummary}`
                                                                                        : allOff
                                                                                            ? `Active: Kiosk off${total > 1 ? ` on all ${total}` : ''} · ${stateSummary}`
                                                                                            : `Mixed: ${on} on, ${total - on} off · ${stateSummary}`}
                                                                            </div>
                                                                        </div>
                                                                    );
                                                                })()}

                                                                {/* Screen rotation policy - rotation_lock field, Monitoring 6.0.5+.
                                                                    The active policy of the SELECTED devices is shown on the buttons themselves: green tint
                                                                    when every selected device is player-controlled, red tint when every one is locked, both
                                                                    neutral when the selection is mixed. The status line adds what the boxes actually report
                                                                    (hw_fingerprint.app_rotation), which lags the field until Monitoring applies it. */}
                                                                {(() => {
                                                                    const selected = selectedDevices.map(id => devices[id]).filter(Boolean);
                                                                    const locked = selected.filter(d => d?.rotation_lock === 'landscape').length;
                                                                    const total = selected.length;
                                                                    const allLocked = total > 0 && locked === total;
                                                                    const allPlayer = total > 0 && locked === 0;
                                                                    const reported = selected.map(d => d?.hw_fingerprint?.app_rotation).filter(Boolean);
                                                                    const reportedLocked = reported.filter(v => v === 'force_land').length;
                                                                    const reportedSummary = reported.length === 0
                                                                        ? 'box value not reported yet'
                                                                        : reportedLocked === 0
                                                                            ? `box reports original${reported.length < total ? ` (${reported.length}/${total})` : ''}`
                                                                            : reportedLocked === reported.length
                                                                                ? `box reports force_land${reported.length < total ? ` (${reported.length}/${total})` : ''}`
                                                                                : `box reports force_land on ${reportedLocked}/${reported.length}`;
                                                                    const activeGreen = 'bg-green-500/15 border-green-500/40 text-green-300 hover:bg-green-500/25';
                                                                    const activeRed = 'bg-red-500/15 border-red-500/40 text-red-300 hover:bg-red-500/25';
                                                                    return (
                                                                        <div className="rounded-lg border border-white/[0.06] p-3">
                                                                            <div className="text-[11px] uppercase tracking-wider text-gray-500 mb-2 inline-flex items-center gap-1.5">
                                                                                <FiRotateCcw /> Screen Rotation
                                                                            </div>
                                                                            <div className="flex flex-wrap gap-2">
                                                                                <button
                                                                                    onClick={() => bulkSetRotationLock(false)}
                                                                                    aria-pressed={allPlayer}
                                                                                    className={`btn-secondary ${allPlayer ? activeGreen : ''}`}
                                                                                    title="persist.sys.app.rotation=original: portrait and 180 flip follow the signage playlist"
                                                                                >
                                                                                    Player Controls
                                                                                </button>
                                                                                <button
                                                                                    onClick={() => bulkSetRotationLock(true)}
                                                                                    aria-pressed={allLocked}
                                                                                    className={`btn-secondary ${allLocked ? activeRed : ''}`}
                                                                                    title="persist.sys.app.rotation=force_land: screen stays landscape, playlist orientation ignored"
                                                                                >
                                                                                    Lock Landscape
                                                                                </button>
                                                                            </div>
                                                                            <div className="text-[11px] text-gray-500 mt-2">
                                                                                {total === 0
                                                                                    ? 'Select devices to see their rotation policy'
                                                                                    : allPlayer
                                                                                        ? `Active: Player controls${total > 1 ? ` on all ${total}` : ''} · ${reportedSummary}`
                                                                                        : allLocked
                                                                                            ? `Active: Locked landscape${total > 1 ? ` on all ${total}` : ''} · ${reportedSummary}`
                                                                                            : `Mixed: ${locked} locked, ${total - locked} player-controlled · ${reportedSummary}`}
                                                                            </div>
                                                                        </div>
                                                                    );
                                                                })()}

                                                                {/* Maintenance - always available. Free Up Storage evicts all app caches AND clears
                                                                    /sdcard/Download (confirm modal); HW fingerprint is a harmless read-only collection
                                                                    on the box (Monitoring 6.0.4+), so no confirmation. Both are bulk-safe. */}
                                                                <div className="rounded-lg border border-white/[0.06] p-3">
                                                                    <div className="text-[11px] uppercase tracking-wider text-gray-500 mb-2 inline-flex items-center gap-1.5">
                                                                        <FiSettings /> Maintenance
                                                                    </div>
                                                                    <div className="flex flex-wrap gap-2">
                                                                        <button
                                                                            onClick={() => setIsFreeStorageConfirmOpen(true)}
                                                                            className="btn-secondary"
                                                                            title="Evicts all app caches and clears /sdcard/Download on the selected device(s)"
                                                                        >
                                                                            <FiTrash2 /> Free Up Storage
                                                                        </button>
                                                                        <button
                                                                            onClick={() => handleSendCommand({value:'hw_fingerprint', successMsg:'Hardware fingerprint requested (Monitoring 6.0.4+)', errorMsg:'Hardware fingerprint request failed'})}
                                                                            className="btn-secondary"
                                                                            title="Re-collects the hardware fingerprint (hw_hash, board, eMMC, partitions) on the selected device(s)"
                                                                        >
                                                                            <FiCpu /> Collect HW Fingerprint
                                                                        </button>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </details>

                                                    {/* Remote Diagnostics - Screenshot & Logs */}
                                                    <details
                                                        open
                                                        className="card-inset p-4 group"
                                                        style={{ display: activeCommandCategory === 'diagnostics' ? 'block' : 'none' }}
                                                    >
                                                        <summary
                                                            className="flex items-center justify-between cursor-pointer select-none text-white">
                                                            <span className="flex items-center gap-2 font-medium">
                                                                <FiCamera className="text-brand-400"/>Remote&nbsp;Diagnostics
                                                            </span>
                                                            <FiChevronDown className="transition-transform duration-200 group-open:rotate-180"/>
                                                        </summary>
                                                        <div className="w-full mt-4">
                                                            <div className="flex flex-row gap-2 flex-wrap">
                                                                {/* Screenshot */}
                                                                <button
                                                                    onClick={() => handleSendCommand({value:'screenshot', successMsg:'Screenshot command sent', errorMsg:'Screenshot command failed'})}
                                                                    className="btn-secondary"
                                                                >
                                                                    <FiCamera className="w-4 h-4"/>
                                                                    Take Screenshot
                                                                </button>

                                                                {/* Get Logs */}
                                                                <button
                                                                    onClick={() => handleSendCommand({value:'logs:500', successMsg:'Logs command sent', errorMsg:'Logs command failed'})}
                                                                    className="btn-secondary"
                                                                >
                                                                    <FiFileText className="w-4 h-4"/>
                                                                    Get Logs (500 lines)
                                                                </button>

                                                                {/* Get More Logs */}
                                                                <button
                                                                    onClick={() => handleSendCommand({value:'logs:2000', successMsg:'Logs command sent', errorMsg:'Logs command failed'})}
                                                                    className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-red-500/15 border border-red-500/40 text-red-300 hover:bg-red-500/25 transition-colors"
                                                                >
                                                                    <FiFileText className="w-4 h-4"/>
                                                                    Get Logs (2000 lines)
                                                                </button>
                                                            </div>

                                                            {/* Display last screenshot/log results */}
                                                            <div className="mt-4 pt-4 border-t border-gray-600">
                                                                <p className="text-sm text-gray-400 mb-2">Last captured:</p>

                                                                {/* Screenshot result */}
                                                                <div className="flex items-center gap-2 mb-2">
                                                                    <FiCamera className="text-brand-400 w-4 h-4"/>
                                                                    <span className="text-sm text-gray-300">Screenshot:</span>
                                                                    {(() => {
                                                                        const firstDevice = selectedDevices[0];
                                                                        const screenshot = firstDevice ? devices[firstDevice]?.last_screenshot : null;
                                                                        const time = firstDevice ? devices[firstDevice]?.last_screenshot_time : null;
                                                                        if (screenshot) {
                                                                            return (
                                                                                <>
                                                                                    <a href={screenshot} target="_blank" rel="noopener noreferrer"
                                                                                       className="text-xs text-brand-400 hover:text-brand underline">
                                                                                        View
                                                                                    </a>
                                                                                    <button
                                                                                        onClick={() => handleDeleteDiagnosticFile(firstDevice, 'screenshot')}
                                                                                        className="text-xs text-red-400 hover:text-red-300 ml-2"
                                                                                        title="Delete screenshot from Wasabi"
                                                                                    >
                                                                                        <FiTrash2 className="w-3 h-3 inline"/>
                                                                                    </button>
                                                                                    {time && <span className="text-xs text-gray-500 ml-1">({new Date(time).toLocaleString()})</span>}
                                                                                </>
                                                                            );
                                                                        }
                                                                        return <span className="text-xs text-gray-500">None</span>;
                                                                    })()}
                                                                </div>

                                                                {/* Logs result */}
                                                                <div className="flex items-center gap-2">
                                                                    <FiFileText className="text-orange-400 w-4 h-4"/>
                                                                    <span className="text-sm text-gray-300">Logs:</span>
                                                                    {(() => {
                                                                        const firstDevice = selectedDevices[0];
                                                                        const log = firstDevice ? devices[firstDevice]?.last_log : null;
                                                                        const time = firstDevice ? devices[firstDevice]?.last_log_time : null;
                                                                        if (log) {
                                                                            return (
                                                                                <>
                                                                                    <a href={log} target="_blank" rel="noopener noreferrer"
                                                                                       className="text-xs text-brand-400 hover:text-brand underline">
                                                                                        View
                                                                                    </a>
                                                                                    <button
                                                                                        onClick={() => handleDeleteDiagnosticFile(firstDevice, 'log')}
                                                                                        className="text-xs text-red-400 hover:text-red-300 ml-2"
                                                                                        title="Delete log from Wasabi"
                                                                                    >
                                                                                        <FiTrash2 className="w-3 h-3 inline"/>
                                                                                    </button>
                                                                                    {time && <span className="text-xs text-gray-500 ml-1">({new Date(time).toLocaleString()})</span>}
                                                                                </>
                                                                            );
                                                                        }
                                                                        return <span className="text-xs text-gray-500">None</span>;
                                                                    })()}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </details>

                                                    {/* Set Volume */}
                                                    <details
                                                        open
                                                        className="card-inset p-4 group"
                                                        style={{ display: activeCommandCategory === 'audio' ? 'block' : 'none' }}
                                                    >
                                                        <summary
                                                            className="flex items-center justify-between cursor-pointer select-none text-white">
                                                            {/* Left Side: Icon + Label */}
                                                            <span className="flex items-center gap-2 font-medium">
                                            <FiVolume2 className="text-brand-400"/>Audio&nbsp;Control</span>

                                                            {/* Right Side: Status + Chevron together */}
                                                            <span className="flex items-center gap-3">
                                            <FiChevronDown
                                                className="transition-transform duration-200 group-open:rotate-180"/>
                                        </span>
                                                        </summary>
                                                        <div className="mt-4">
                                                            <CustomSlider
                                                                label="Set Volume"
                                                                value={commandVolumeLevel}
                                                                onChange={setCommandVolumeLevel}
                                                            />
                                                        </div>

                                                        <div className="mt-6 flex justify-center">
                                                            <button
                                                                onClick={handleSetVolume}
                                                                className="btn-secondary"
                                                            >
                                                                <FiSend className="w-5 h-5"/>
                                                                Send Command
                                                            </button>
                                                        </div>
                                                    </details>

                                                    {/* App Management - placeholder sections */}
                                                    <details
                                                        open
                                                        className="card-inset p-4 group"
                                                        style={{ display: activeCommandCategory === 'apps' ? 'block' : 'none' }}
                                                    >
                                                        <summary
                                                            className="flex items-center justify-between cursor-pointer select-none text-white">
                                                            {/* Left Side: Icon + Label */}
                                                            <span className="flex items-center gap-2 font-medium">
                                            <FiPackage className="text-brand-400"/> App&nbsp;Management</span>

                                                            {/* Right Side: Status + Chevron together */}
                                                            <span className="flex items-center gap-3">
                                            <FiChevronDown
                                                className="transition-transform duration-200 group-open:rotate-180"/>
                                        </span>
                                                        </summary>
                                                        <div className="mt-4 flex flex-col gap-3">
                                                            {sharedCommands.includes('allowInstall') && (
                                                                <details
                                                                    className="card-inset p-4">
                                                                    <summary
                                                                        className="flex items-center justify-between cursor-pointer select-none text-white">
                                                                        {/* Left Side: Icon + Label */}
                                                                        <span
                                                                            className="flex items-center gap-2 font-medium">
                                                        <FiPlus
                                                            className="text-brand-400"/>App&nbsp;Installation</span>
                                                                        {/* Right Side: Status + Chevron together */}
                                                                        <span className="flex items-center gap-3">
                                                        <FiChevronDown
                                                            className="transition-transform duration-200 group-open:rotate-180"/>
                                                    </span>
                                                                    </summary>
                                                                    <div className="mt-3 space-y-4">
                                                                        {/* App tile picker — `selectedInstallApp` is
                                                                            the safe key (e.g. `rs_musicdj_player`),
                                                                            kept the same so the existing handler at
                                                                            install: time still uses
                                                                            `apps[selectedInstallApp].originalPackageName`. */}
                                                                        <AppTilePicker
                                                                            items={installableApps.map(([id, app]) => ({
                                                                                value: id,
                                                                                label: app.name || id,
                                                                                iconUrl: app.iconUrl,
                                                                                subtitle: app.latestVersion ? `v${app.latestVersion}` : null,
                                                                            }))}
                                                                            value={selectedInstallApp}
                                                                            onSelect={setSelectedInstallApp}
                                                                            emptyText="No apps in the store yet — upload one in Apps Management."
                                                                        />

                                                                        {/* Version + Send appear only after a tile is
                                                                            picked, so the action area doesn't sit
                                                                            empty above the picker. */}
                                                                        {!!selectedInstallApp.length && apps[selectedInstallApp] && (
                                                                            <div className="card-inset p-4 flex flex-col md:flex-row md:items-end gap-3">
                                                                                <div className="flex items-center gap-3 min-w-0 flex-1">
                                                                                    <div className="w-10 h-10 rounded-lg bg-white/[0.04] flex items-center justify-center overflow-hidden shrink-0">
                                                                                        {apps[selectedInstallApp].iconUrl ? (
                                                                                            <img
                                                                                                src={apps[selectedInstallApp].iconUrl}
                                                                                                alt=""
                                                                                                className="w-full h-full object-contain"
                                                                                                onError={(e) => { e.currentTarget.style.display = 'none'; }}
                                                                                            />
                                                                                        ) : (
                                                                                            <FiPackage className="text-gray-500" />
                                                                                        )}
                                                                                    </div>
                                                                                    <div className="min-w-0">
                                                                                        <div className="text-[11px] uppercase tracking-wider text-gray-500">Selected</div>
                                                                                        <div className="text-sm font-medium text-white truncate">
                                                                                            {apps[selectedInstallApp].name || selectedInstallApp}
                                                                                        </div>
                                                                                    </div>
                                                                                </div>
                                                                                <div className="md:w-56">
                                                                                    <CustomDropdown
                                                                                        label="Version"
                                                                                        value={selectedInstallVersion}
                                                                                        disabled={singleVersion}
                                                                                        onChange={setSelectedInstallVersion}
                                                                                        options={selectedAppVersions.map((version) => ({
                                                                                            label: version,
                                                                                            value: version,
                                                                                        }))}
                                                                                    />
                                                                                </div>
                                                                                <button
                                                                                    disabled={!selectedInstallApp || !selectedInstallVersion}
                                                                                    onClick={() =>
                                                                                        handleSendCommand({value:`install:${apps[selectedInstallApp].originalPackageName}:${selectedInstallVersion}`, successMsg:'App installation command sending successful', errorMsg:'App installation failed'})
                                                                                    }
                                                                                    className="btn-primary whitespace-nowrap"
                                                                                >
                                                                                    Install
                                                                                </button>
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                </details>
                                                            )}
                                                            {/* App Re-Installation — hidden for now, the
                                                                `reinstall:` command isn't reliably handled
                                                                on the device side yet. To bring it back, drop
                                                                the `false &&` gate. */}
                                                            {false && sharedCommands.includes('allowReinstall') && (
                                                                <details
                                                                    className="card-inset p-4">
                                                                    <summary
                                                                        className="flex items-center justify-between cursor-pointer select-none text-white">
                                                                        {/* Left Side: Icon + Label */}
                                                                        <span
                                                                            className="flex items-center gap-2 font-medium">
                                                        <FiRefreshCw
                                                            className="text-brand-400"/>App&nbsp;Re-Installation</span>
                                                                        {/* Right Side: Status + Chevron together */}
                                                                        <span className="flex items-center gap-3">
                                                        <FiChevronDown
                                                            className="transition-transform duration-200 group-open:rotate-180"/>
                                                    </span>
                                                                    </summary>
                                                                    <div
                                                                        className="flex flex-col md:flex-row gap-4 w-full mt-3">
                                                                        {/* App Name Dropdown */}
                                                                        <div className="flex-1">
                                                                            <CustomDropdown
                                                                                label="App Name"
                                                                                disabled={!commonApps.length}
                                                                                value={selectedReinstallApp}
                                                                                onChange={setSelectedReinstallApp}
                                                                                options={commonApps.map((app) => ({
                                                                                    label: app.appName || app.packageName,
                                                                                    value: app.packageName,
                                                                                }))}
                                                                            />
                                                                        </div>
                                                                    </div>

                                                                    {/* Send Button */}
                                                                    <div className="mt-6 flex justify-center">
                                                                        <button
                                                                            disabled={!selectedReinstallApp}
                                                                            onClick={() =>
                                                                                handleSendCommand({value: `reinstall:${selectedReinstallApp}`, successMsg:'App re-installation command sending successful', errorMsg:'App re-installation failed'})
                                                                            }
                                                                            className="btn-primary"
                                                                        >
                                                                            Send Command
                                                                        </button>
                                                                    </div>
                                                                </details>
                                                            )}
                                                            {sharedCommands.includes('allowDelete') && (
                                                                <details
                                                                    className="card-inset p-4">
                                                                    <summary
                                                                        className="flex items-center justify-between cursor-pointer select-none text-white">
                                                                        {/* Left Side: Icon + Label */}
                                                                        <span
                                                                            className="flex items-center gap-2 font-medium">
                                                        <FiTrash2
                                                            className="text-brand-400"/>Delete&nbsp;App</span>
                                                                        {/* Right Side: Status + Chevron together */}
                                                                        <span className="flex items-center gap-3">
                                                        <FiChevronDown
                                                            className="transition-transform duration-200 group-open:rotate-180"/>
                                                    </span>
                                                                    </summary>
                                                                    <div className="mt-3 space-y-4">
                                                                        {/* Tile picker — sourced from `commonApps`
                                                                            (apps installed on every selected device).
                                                                            Icon is looked up from the app store via
                                                                            the safe-key form of the package name; if
                                                                            the app isn't in the store, the picker
                                                                            falls back to the package icon. The
                                                                            monitoring service is filtered out — it IS
                                                                            the dashboard's control channel, so
                                                                            uninstalling it bricks remote management. */}
                                                                        <AppTilePicker
                                                                            items={commonApps
                                                                                .filter((app) => !isMonitoringPkg(app.packageName))
                                                                                .map((app) => ({
                                                                                    value: app.packageName,
                                                                                    label: app.appName || app.packageName,
                                                                                    iconUrl: apps[app.packageName.replace(/\./g, '_')]?.iconUrl,
                                                                                    subtitle: app.appVersion ? `v${app.appVersion}` : null,
                                                                                }))}
                                                                            value={selectedDeletionApp}
                                                                            onSelect={setSelectedDeletionApp}
                                                                            emptyText="No apps installed on the selected device(s)."
                                                                        />

                                                                        {!!selectedDeletionApp && selectedDeletionApp.length > 0 && (
                                                                            <div className="card-inset p-4 flex flex-col md:flex-row md:items-center gap-3">
                                                                                <div className="flex items-center gap-3 min-w-0 flex-1">
                                                                                    <div className="w-10 h-10 rounded-lg bg-white/[0.04] flex items-center justify-center overflow-hidden shrink-0">
                                                                                        {apps[selectedDeletionApp.replace(/\./g, '_')]?.iconUrl ? (
                                                                                            <img
                                                                                                src={apps[selectedDeletionApp.replace(/\./g, '_')].iconUrl}
                                                                                                alt=""
                                                                                                className="w-full h-full object-contain"
                                                                                                onError={(e) => { e.currentTarget.style.display = 'none'; }}
                                                                                            />
                                                                                        ) : (
                                                                                            <FiPackage className="text-gray-500" />
                                                                                        )}
                                                                                    </div>
                                                                                    <div className="min-w-0">
                                                                                        <div className="text-[11px] uppercase tracking-wider text-gray-500">Selected</div>
                                                                                        <div className="text-sm font-medium text-white truncate">
                                                                                            {commonApps.find((a) => a.packageName === selectedDeletionApp)?.appName || selectedDeletionApp}
                                                                                        </div>
                                                                                    </div>
                                                                                </div>
                                                                                <button
                                                                                    onClick={() => setIsUninstallConfirmOpen(true)}
                                                                                    className="btn-primary whitespace-nowrap"
                                                                                >
                                                                                    Uninstall
                                                                                </button>
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                </details>
                                                            )}
                                                            {/* Change App Version — hidden for now, the
                                                                version-pinning flow needs more work on the
                                                                device side. To bring it back, drop the
                                                                `false &&` gate. */}
                                                            {false && sharedCommands.includes('allowVersionChange') && (
                                                                <details
                                                                    className="card-inset p-4">
                                                                    <summary
                                                                        className="flex items-center justify-between cursor-pointer select-none text-white">
                                                                        {/* Left Side: Icon + Label */}
                                                                        <span
                                                                            className="flex items-center gap-2 font-medium">
                                                        <FiEdit3
                                                            className="text-brand-400"/>Change&nbsp;App&nbsp;Version</span>
                                                                        {/* Right Side: Status + Chevron together */}
                                                                        <span className="flex items-center gap-3">
                                                        <FiChevronDown
                                                            className="transition-transform duration-200 group-open:rotate-180"/>
                                                    </span>
                                                                    </summary>
                                                                    <div
                                                                        className="flex flex-col md:flex-row gap-4 w-full mt-3">
                                                                        {/* App Name Dropdown */}
                                                                        <div className="flex-1">
                                                                            <CustomDropdown
                                                                                label="App Name"
                                                                                value={selectedAppForVersionChange}
                                                                                onChange={setSelectedAppForVersionChange}
                                                                                options={commonApps.map((app) => ({
                                                                                    label: app.appName || app.packageName,
                                                                                    value: app.packageName,
                                                                                }))}
                                                                            />
                                                                        </div>
                                                                        {/* App Version Dropdown */}
                                                                        {!!selectedAppForVersionChange.length && (
                                                                            <div className="flex-1">
                                                                                <CustomDropdown
                                                                                    label="App Version"
                                                                                    disabled={singleVersionForChange}
                                                                                    value={selectedChangingVersion}
                                                                                    onChange={setSelectedChangingVersion}
                                                                                    options={
                                                                                        selectedAppVersionsForChange.map((version) => ({
                                                                                            label: version,
                                                                                            value: version,
                                                                                        }))
                                                                                    }
                                                                                />
                                                                            </div>
                                                                        )}
                                                                    </div>

                                                                    {/* Send Button */}
                                                                    <div className="mt-6 flex justify-center">
                                                                        <button
                                                                            disabled={!selectedAppForVersionChange || !selectedChangingVersion}
                                                                            onClick={() =>
                                                                                handleSendCommand({value:`install:${selectedAppForVersionChange}:${selectedChangingVersion}`, successMsg:'App version change command sending successful', errorMsg:'App version change failed'})
                                                                            }
                                                                            className="btn-primary"
                                                                        >
                                                                            Send Command
                                                                        </button>
                                                                    </div>
                                                                </details>
                                                            )}
                                                            {/*App Control*/}
                                                            {sharedCommands.includes('allowOpen') && (
                                                                <details
                                                                    className="card-inset p-4">
                                                                    <summary
                                                                        className="flex items-center justify-between cursor-pointer select-none text-white">
                                                                        {/* Left Side: Icon + Label */}
                                                                        <span
                                                                            className="flex items-center gap-2 font-medium">
                                                        <FiPower
                                                            className="text-brand-400"/>Open&nbsp;App</span>
                                                                        {/* Right Side: Status + Chevron together */}
                                                                        <span className="flex items-center gap-3">
                                                        <FiChevronDown
                                                            className="transition-transform duration-200 group-open:rotate-180"/>
                                                    </span>
                                                                    </summary>
                                                                    <div className="mt-3 space-y-4">
                                                                        <AppTilePicker
                                                                            items={commonApps.map((app) => ({
                                                                                value: app.packageName,
                                                                                label: app.appName || app.packageName,
                                                                                iconUrl: apps[app.packageName.replace(/\./g, '_')]?.iconUrl,
                                                                                subtitle: app.appVersion ? `v${app.appVersion}` : null,
                                                                            }))}
                                                                            value={selectedOpenApp}
                                                                            onSelect={setSelectedOpenApp}
                                                                            emptyText="No apps installed on the selected device(s)."
                                                                        />

                                                                        {!!selectedOpenApp && selectedOpenApp.length > 0 && (
                                                                            <div className="card-inset p-4 flex flex-col md:flex-row md:items-center gap-3">
                                                                                <div className="flex items-center gap-3 min-w-0 flex-1">
                                                                                    <div className="w-10 h-10 rounded-lg bg-white/[0.04] flex items-center justify-center overflow-hidden shrink-0">
                                                                                        {apps[selectedOpenApp.replace(/\./g, '_')]?.iconUrl ? (
                                                                                            <img
                                                                                                src={apps[selectedOpenApp.replace(/\./g, '_')].iconUrl}
                                                                                                alt=""
                                                                                                className="w-full h-full object-contain"
                                                                                                onError={(e) => { e.currentTarget.style.display = 'none'; }}
                                                                                            />
                                                                                        ) : (
                                                                                            <FiPackage className="text-gray-500" />
                                                                                        )}
                                                                                    </div>
                                                                                    <div className="min-w-0">
                                                                                        <div className="text-[11px] uppercase tracking-wider text-gray-500">Selected</div>
                                                                                        <div className="text-sm font-medium text-white truncate">
                                                                                            {commonApps.find((a) => a.packageName === selectedOpenApp)?.appName || selectedOpenApp}
                                                                                        </div>
                                                                                    </div>
                                                                                </div>
                                                                                <button
                                                                                    onClick={() =>
                                                                                        handleSendCommand({value:`open:${selectedOpenApp}`, successMsg:'App opening command sending successful', errorMsg:'App opening failed'})
                                                                                    }
                                                                                    className="btn-primary whitespace-nowrap"
                                                                                >
                                                                                    Open
                                                                                </button>
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                </details>
                                                            )}
                                                            {sharedCommands.includes('allowRestart') && (
                                                                <details
                                                                    className="card-inset p-4">
                                                                    <summary
                                                                        className="flex items-center justify-between cursor-pointer select-none text-white">
                                                                        {/* Left Side: Icon + Label */}
                                                                        <span
                                                                            className="flex items-center gap-2 font-medium">
                                                        <FiRotateCcw
                                                            className="text-brand-400"/>Restart&nbsp;App</span>
                                                                        {/* Right Side: Status + Chevron together */}
                                                                        <span className="flex items-center gap-3">
                                                        <FiChevronDown
                                                            className="transition-transform duration-200 group-open:rotate-180"/>
                                                    </span>
                                                                    </summary>
                                                                    <div className="mt-3 space-y-4">
                                                                        <AppTilePicker
                                                                            items={commonApps.map((app) => ({
                                                                                value: app.packageName,
                                                                                label: app.appName || app.packageName,
                                                                                iconUrl: apps[app.packageName.replace(/\./g, '_')]?.iconUrl,
                                                                                subtitle: app.appVersion ? `v${app.appVersion}` : null,
                                                                            }))}
                                                                            value={selectedRestartApp}
                                                                            onSelect={setSelectedRestartApp}
                                                                            emptyText="No apps installed on the selected device(s)."
                                                                        />

                                                                        {!!selectedRestartApp && selectedRestartApp.length > 0 && (
                                                                            <div className="card-inset p-4 flex flex-col md:flex-row md:items-center gap-3">
                                                                                <div className="flex items-center gap-3 min-w-0 flex-1">
                                                                                    <div className="w-10 h-10 rounded-lg bg-white/[0.04] flex items-center justify-center overflow-hidden shrink-0">
                                                                                        {apps[selectedRestartApp.replace(/\./g, '_')]?.iconUrl ? (
                                                                                            <img
                                                                                                src={apps[selectedRestartApp.replace(/\./g, '_')].iconUrl}
                                                                                                alt=""
                                                                                                className="w-full h-full object-contain"
                                                                                                onError={(e) => { e.currentTarget.style.display = 'none'; }}
                                                                                            />
                                                                                        ) : (
                                                                                            <FiPackage className="text-gray-500" />
                                                                                        )}
                                                                                    </div>
                                                                                    <div className="min-w-0">
                                                                                        <div className="text-[11px] uppercase tracking-wider text-gray-500">Selected</div>
                                                                                        <div className="text-sm font-medium text-white truncate">
                                                                                            {commonApps.find((a) => a.packageName === selectedRestartApp)?.appName || selectedRestartApp}
                                                                                        </div>
                                                                                    </div>
                                                                                </div>
                                                                                <button
                                                                                    onClick={() =>
                                                                                        handleSendCommand({value:`restart:${selectedRestartApp}`, successMsg:'App restarting command sending successful', errorMsg:'App restart failed'})
                                                                                    }
                                                                                    className="btn-primary whitespace-nowrap"
                                                                                >
                                                                                    Restart
                                                                                </button>
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                </details>
                                                            )}
                                                            {sharedCommands.includes('allowClose') && (
                                                                <details
                                                                    className="card-inset p-4">
                                                                    <summary
                                                                        className="flex items-center justify-between cursor-pointer select-none text-white">
                                                                        {/* Left Side: Icon + Label */}
                                                                        <span
                                                                            className="flex items-center gap-2 font-medium">
                                                        <FiXCircle
                                                            className="text-brand-400"/>Close&nbsp;App</span>
                                                                        {/* Right Side: Status + Chevron together */}
                                                                        <span className="flex items-center gap-3">
                                                        <FiChevronDown
                                                            className="transition-transform duration-200 group-open:rotate-180"/>
                                                    </span>
                                                                    </summary>
                                                                    <div className="mt-3 space-y-4">
                                                                        {/* Monitoring service is filtered out —
                                                                            closing it cuts off remote management
                                                                            until the device reboots and the
                                                                            watchdog brings it back. */}
                                                                        <AppTilePicker
                                                                            items={commonApps
                                                                                .filter((app) => !isMonitoringPkg(app.packageName))
                                                                                .map((app) => ({
                                                                                    value: app.packageName,
                                                                                    label: app.appName || app.packageName,
                                                                                    iconUrl: apps[app.packageName.replace(/\./g, '_')]?.iconUrl,
                                                                                    subtitle: app.appVersion ? `v${app.appVersion}` : null,
                                                                                }))}
                                                                            value={selectedCloseApp}
                                                                            onSelect={setSelectedCloseApp}
                                                                            emptyText="No apps installed on the selected device(s)."
                                                                        />

                                                                        {!!selectedCloseApp && selectedCloseApp.length > 0 && (
                                                                            <div className="card-inset p-4 flex flex-col md:flex-row md:items-center gap-3">
                                                                                <div className="flex items-center gap-3 min-w-0 flex-1">
                                                                                    <div className="w-10 h-10 rounded-lg bg-white/[0.04] flex items-center justify-center overflow-hidden shrink-0">
                                                                                        {apps[selectedCloseApp.replace(/\./g, '_')]?.iconUrl ? (
                                                                                            <img
                                                                                                src={apps[selectedCloseApp.replace(/\./g, '_')].iconUrl}
                                                                                                alt=""
                                                                                                className="w-full h-full object-contain"
                                                                                                onError={(e) => { e.currentTarget.style.display = 'none'; }}
                                                                                            />
                                                                                        ) : (
                                                                                            <FiPackage className="text-gray-500" />
                                                                                        )}
                                                                                    </div>
                                                                                    <div className="min-w-0">
                                                                                        <div className="text-[11px] uppercase tracking-wider text-gray-500">Selected</div>
                                                                                        <div className="text-sm font-medium text-white truncate">
                                                                                            {commonApps.find((a) => a.packageName === selectedCloseApp)?.appName || selectedCloseApp}
                                                                                        </div>
                                                                                    </div>
                                                                                </div>
                                                                                <button
                                                                                    onClick={() =>
                                                                                        handleSendCommand({value:`close:${selectedCloseApp}`, successMsg:'App closing command sending successful', errorMsg:'App closing failed'})
                                                                                    }
                                                                                    className="btn-primary whitespace-nowrap"
                                                                                >
                                                                                    Close
                                                                                </button>
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                </details>
                                                            )}
                                                        </div>
                                                    </details>

                                                    {/* File Transfer Section */}
                                                    <details
                                                        open
                                                        className="card-inset p-4 group"
                                                        style={{ display: activeCommandCategory === 'transfer' ? 'block' : 'none' }}
                                                    >
                                                        <summary
                                                            className="flex items-center justify-between cursor-pointer select-none text-white">
                                                            <span className="flex items-center gap-2 font-medium">
                                                                <FiUpload className="text-cyan-400"/>File&nbsp;Transfer
                                                            </span>
                                                            <FiChevronDown className="transition-transform duration-200 group-open:rotate-180"/>
                                                        </summary>
                                                        <div className="w-full mt-4 space-y-4">
                                                            <p className="text-sm text-gray-400">
                                                                Upload a file to Wasabi and send download command to selected device(s).
                                                            </p>

                                                            {/* File Input */}
                                                            <div>
                                                                <label className="block text-sm mb-2 text-gray-300">Select File</label>
                                                                <input
                                                                    ref={fileInputRef}
                                                                    type="file"
                                                                    onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                                                                    className="input-field file:mr-4 file:py-1 file:px-3 file:rounded file:border-0 file:bg-brand file:text-white file:cursor-pointer hover:file:bg-brand-400"
                                                                />
                                                                {uploadFile && (
                                                                    <p className="text-xs text-gray-400 mt-1">
                                                                        Selected: {uploadFile.name} ({(uploadFile.size / 1024 / 1024).toFixed(2)} MB)
                                                                    </p>
                                                                )}
                                                            </div>

                                                            {/* Destination Path */}
                                                            <div>
                                                                <label className="block text-sm mb-2 text-gray-300">Destination Path on Device</label>
                                                                <input
                                                                    type="text"
                                                                    value={uploadDestPath}
                                                                    onChange={(e) => setUploadDestPath(e.target.value)}
                                                                    placeholder="/sdcard/Download/"
                                                                    className="input-field"
                                                                />
                                                                <p className="text-xs text-gray-500 mt-1">
                                                                    Filename will be appended automatically if path ends with /
                                                                </p>
                                                            </div>

                                                            {/* Upload Button */}
                                                            <div className="flex justify-center">
                                                                <button
                                                                    onClick={handleFileUpload}
                                                                    disabled={!uploadFile || isUploading}
                                                                    className="btn-primary"
                                                                >
                                                                    {isUploading ? (
                                                                        <>
                                                                            <FiRefreshCw className="w-4 h-4 animate-spin"/>
                                                                            Uploading...
                                                                        </>
                                                                    ) : (
                                                                        <>
                                                                            <FiUpload className="w-4 h-4"/>
                                                                            Upload & Send to Device{selectedDevices.length > 1 ? 's' : ''}
                                                                        </>
                                                                    )}
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </details>
                                                </section>
                                            </div>
                                        </div>
                                    </div>
                        </div>
                                    )}


                                    <div>
                                        {/* Header Section */}
                                        <div
                                            className="card-chrome mb-4">
                                            <div className="flex justify-between items-center mb-6">
                                                <div>
                                                    <h1 className="inline-flex items-center gap-3">
                                                        <span className="brand-icon-badge"><FiMonitor /></span>
                                                        <span className="text-xl font-semibold text-white">Device Fleet</span>
                                                    </h1>
                                                    <div className="text-sm text-gray-400 mt-1">
                                                        Total Devices: {Object.keys(devices).length} |
                                                        Online: {Object.values(devices).filter(d => d.status === "online").length}
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Update Status Summary Cards */}
                                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                                                {/* Player Card */}
                                                <button
                                                    onClick={() => setUpdateFilter(updateFilter === 'player' ? '' : 'player')}
                                                    className={`p-3 rounded-lg border transition-all text-left ${
                                                        updateFilter === 'player'
                                                            ? 'bg-brand/15 border-brand/40'
                                                            : 'bg-white/[0.03] border-white/[0.08] hover:border-white/[0.15]'
                                                    }`}
                                                >
                                                    <div className="text-xs text-gray-400 mb-1">Player</div>
                                                    <div className="flex items-center justify-between">
                                                        <span className="text-sm font-medium text-white">
                                                            v{updateStats.player.latestVersion || '?'}
                                                        </span>
                                                        {updateStats.player.needsUpdate > 0 ? (
                                                            <span className="px-2 py-0.5 bg-orange-500/20 text-orange-400 rounded text-xs font-medium">
                                                                {updateStats.player.needsUpdate} outdated
                                                            </span>
                                                        ) : (
                                                            <span className="px-2 py-0.5 bg-green-500/20 text-green-400 rounded text-xs font-medium">
                                                                All OK
                                                            </span>
                                                        )}
                                                    </div>
                                                </button>

                                                {/* Updater Card */}
                                                <button
                                                    onClick={() => setUpdateFilter(updateFilter === 'updater' ? '' : 'updater')}
                                                    className={`p-3 rounded-lg border transition-all text-left ${
                                                        updateFilter === 'updater'
                                                            ? 'bg-brand/15 border-brand/40'
                                                            : 'bg-white/[0.03] border-white/[0.08] hover:border-white/[0.15]'
                                                    }`}
                                                >
                                                    <div className="text-xs text-gray-400 mb-1">Updater</div>
                                                    <div className="flex items-center justify-between">
                                                        <span className="text-sm font-medium text-white">
                                                            v{updateStats.updater.latestVersion || '?'}
                                                        </span>
                                                        {updateStats.updater.needsUpdate > 0 ? (
                                                            <span className="px-2 py-0.5 bg-orange-500/20 text-orange-400 rounded text-xs font-medium">
                                                                {updateStats.updater.needsUpdate} outdated
                                                            </span>
                                                        ) : (
                                                            <span className="px-2 py-0.5 bg-green-500/20 text-green-400 rounded text-xs font-medium">
                                                                All OK
                                                            </span>
                                                        )}
                                                    </div>
                                                </button>

                                                {/* Monitoring Card */}
                                                <button
                                                    onClick={() => setUpdateFilter(updateFilter === 'monitoring' ? '' : 'monitoring')}
                                                    className={`p-3 rounded-lg border transition-all text-left ${
                                                        updateFilter === 'monitoring'
                                                            ? 'bg-brand/15 border-brand/40'
                                                            : 'bg-white/[0.03] border-white/[0.08] hover:border-white/[0.15]'
                                                    }`}
                                                >
                                                    <div className="text-xs text-gray-400 mb-1">Monitoring</div>
                                                    <div className="flex items-center justify-between">
                                                        <span className="text-sm font-medium text-white">
                                                            v{updateStats.monitoring.latestVersion || '?'}
                                                        </span>
                                                        {updateStats.monitoring.needsUpdate > 0 ? (
                                                            <span className="px-2 py-0.5 bg-orange-500/20 text-orange-400 rounded text-xs font-medium">
                                                                {updateStats.monitoring.needsUpdate} outdated
                                                            </span>
                                                        ) : (
                                                            <span className="px-2 py-0.5 bg-green-500/20 text-green-400 rounded text-xs font-medium">
                                                                All OK
                                                            </span>
                                                        )}
                                                    </div>
                                                </button>

                                                {/* OTA Card */}
                                                <button
                                                    onClick={() => setUpdateFilter(updateFilter === 'ota' ? '' : 'ota')}
                                                    className={`p-3 rounded-lg border transition-all text-left ${
                                                        updateFilter === 'ota'
                                                            ? 'bg-brand/15 border-brand/40'
                                                            : 'bg-white/[0.03] border-white/[0.08] hover:border-white/[0.15]'
                                                    }`}
                                                >
                                                    <div className="text-xs text-gray-400 mb-1">System OTA</div>
                                                    <div className="flex items-center justify-between">
                                                        <span className="text-sm font-medium text-white">
                                                            Firmware
                                                        </span>
                                                        {updateStats.ota.needsUpdate > 0 ? (
                                                            <span className="px-2 py-0.5 bg-orange-500/20 text-orange-400 rounded text-xs font-medium">
                                                                {updateStats.ota.needsUpdate} outdated
                                                            </span>
                                                        ) : (
                                                            <span className="px-2 py-0.5 bg-green-500/20 text-green-400 rounded text-xs font-medium">
                                                                All OK
                                                            </span>
                                                        )}
                                                    </div>
                                                </button>
                                            </div>

                                            {/* Player Health Section - Online devices player status */}
                                            <div className="mb-6 p-4 card-inset">
                                                <div className="flex items-center gap-2 mb-3">
                                                    <FiPlay className="w-4 h-4 text-brand-400" />
                                                    <span className="text-sm font-semibold text-white">Player Health</span>
                                                    <span className="text-xs text-gray-400">
                                                        ({updateStats.playerHealth.onlineDevices} online devices)
                                                    </span>
                                                </div>
                                                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                                    {/* Running */}
                                                    <button
                                                        onClick={() => setUpdateFilter(updateFilter === 'player_running' ? '' : 'player_running')}
                                                        className={`p-2 rounded-lg border transition-all text-left ${
                                                            updateFilter === 'player_running'
                                                                ? 'bg-green-600/30 border-green-500'
                                                                : 'bg-white/[0.03] border-white/[0.08] hover:border-green-500/50'
                                                        }`}
                                                    >
                                                        <div className="flex items-center gap-2">
                                                            <span className="w-2 h-2 rounded-full bg-green-500"></span>
                                                            <span className="text-xs text-gray-300">Running</span>
                                                        </div>
                                                        <div className="text-lg font-bold text-green-400 mt-1">
                                                            {updateStats.playerHealth.running}
                                                        </div>
                                                    </button>

                                                    {/* Stopped */}
                                                    <button
                                                        onClick={() => setUpdateFilter(updateFilter === 'player_stopped' ? '' : 'player_stopped')}
                                                        className={`p-2 rounded-lg border transition-all text-left ${
                                                            updateFilter === 'player_stopped'
                                                                ? 'bg-red-600/30 border-red-500'
                                                                : 'bg-white/[0.03] border-white/[0.08] hover:border-red-500/50'
                                                        }`}
                                                    >
                                                        <div className="flex items-center gap-2">
                                                            <span className="w-2 h-2 rounded-full bg-red-500"></span>
                                                            <span className="text-xs text-gray-300">Stopped</span>
                                                        </div>
                                                        <div className="text-lg font-bold text-red-400 mt-1">
                                                            {updateStats.playerHealth.stopped}
                                                        </div>
                                                    </button>

                                                    {/* Restarted */}
                                                    <button
                                                        onClick={() => setUpdateFilter(updateFilter === 'player_restarted' ? '' : 'player_restarted')}
                                                        className={`p-2 rounded-lg border transition-all text-left ${
                                                            updateFilter === 'player_restarted'
                                                                ? 'bg-yellow-600/30 border-yellow-500'
                                                                : 'bg-white/[0.03] border-white/[0.08] hover:border-yellow-500/50'
                                                        }`}
                                                    >
                                                        <div className="flex items-center gap-2">
                                                            <span className="w-2 h-2 rounded-full bg-yellow-500"></span>
                                                            <span className="text-xs text-gray-300">Restarted</span>
                                                        </div>
                                                        <div className="text-lg font-bold text-yellow-400 mt-1">
                                                            {updateStats.playerHealth.restarted}
                                                        </div>
                                                    </button>

                                                    {/* Not Installed */}
                                                    <button
                                                        onClick={() => setUpdateFilter(updateFilter === 'player_not_installed' ? '' : 'player_not_installed')}
                                                        className={`p-2 rounded-lg border transition-all text-left ${
                                                            updateFilter === 'player_not_installed'
                                                                ? 'bg-gray-600/30 border-gray-500'
                                                                : 'bg-white/[0.03] border-white/[0.08] hover:border-white/[0.15]'
                                                        }`}
                                                    >
                                                        <div className="flex items-center gap-2">
                                                            <span className="w-2 h-2 rounded-full bg-gray-500"></span>
                                                            <span className="text-xs text-gray-300">Not Installed</span>
                                                        </div>
                                                        <div className="text-lg font-bold text-gray-400 mt-1">
                                                            {updateStats.playerHealth.notInstalled}
                                                        </div>
                                                    </button>
                                                </div>

                                                {/* Quick action: Show all NOT running */}
                                                {(updateStats.playerHealth.stopped + updateStats.playerHealth.restarted + updateStats.playerHealth.notInstalled) > 0 && (
                                                    <button
                                                        onClick={() => setUpdateFilter(updateFilter === 'player_not_running' ? '' : 'player_not_running')}
                                                        className={`mt-3 w-full p-2 rounded-lg border transition-all text-center ${
                                                            updateFilter === 'player_not_running'
                                                                ? 'bg-red-600/20 border-red-500 text-red-400'
                                                                : 'bg-white/[0.02] border-white/[0.08] hover:border-red-500/50 text-gray-300 hover:text-red-400'
                                                        }`}
                                                    >
                                                        <span className="text-xs font-medium">
                                                            Show all devices with player NOT running ({updateStats.playerHealth.stopped + updateStats.playerHealth.restarted + updateStats.playerHealth.notInstalled + updateStats.playerHealth.unknown})
                                                        </span>
                                                    </button>
                                                )}
                                            </div>

                                            {/* Active filter indicator */}
                                            {updateFilter && (
                                                <div className="mb-4 flex items-center gap-2">
                                                    <span className="text-sm text-gray-400">
                                                        {updateFilter.startsWith('player_') ? (
                                                            <>Showing online devices with player <strong className="text-brand-400">
                                                                {updateFilter === 'player_running' ? 'running' :
                                                                 updateFilter === 'player_stopped' ? 'stopped' :
                                                                 updateFilter === 'player_restarted' ? 'restarted' :
                                                                 updateFilter === 'player_not_installed' ? 'not installed' :
                                                                 updateFilter === 'player_not_running' ? 'NOT running' : updateFilter}
                                                            </strong></>
                                                        ) : (
                                                            <>Showing devices needing <strong className="text-brand-400">{updateFilter}</strong> update</>
                                                        )}
                                                    </span>
                                                    <button
                                                        onClick={() => setUpdateFilter('')}
                                                        className="text-xs px-2 py-1 bg-gray-600 hover:bg-gray-500 rounded text-white transition"
                                                    >
                                                        Clear filter
                                                    </button>
                                                </div>
                                            )}

                                            {/* Toolbar: on phones the search is its own full-width row with the
                                                filters in a 2-up grid beneath it; from lg up they share one row
                                                (search grows, filters sit to the right). Replaces the old fixed
                                                grid template that broke once a 4th dropdown was added. */}
                                            <div className="flex flex-col lg:flex-row lg:items-end gap-4">
                                                {/* Search — own field, full width on mobile */}
                                                <div className="w-full lg:flex-1 lg:min-w-[260px]">
                                                    <label className="block text-xs uppercase tracking-wider text-gray-400 mb-1.5">
                                                        Search Devices
                                                    </label>
                                                    <div className="relative w-full">
                                                        <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"/>
                                                        <input
                                                            type="text"
                                                            placeholder="E.g. 91.150.105.22 or C4:4E:AC:20:5D:E7..."
                                                            value={searchQuery}
                                                            onChange={e => {
                                                                const q = e.target.value;
                                                                setSearchParams(params => {
                                                                    if (q) params.set('q', q);
                                                                    else params.delete('q');
                                                                    return params;
                                                                });
                                                            }}
                                                            className="input-field pl-10"
                                                        />
                                                    </div>
                                                </div>

                                                {/* Filters — 2-up grid on phones, single row from lg up */}
                                                <div className="grid grid-cols-2 gap-3 lg:flex lg:flex-nowrap lg:gap-3">
                                                    <div className="w-full lg:w-[170px]">
                                                        <CustomDropdown
                                                            label="Device Group"
                                                            value={selectedGroup ?? ''}
                                                            onChange={g => {
                                                                setSearchParams(params => {
                                                                    if (g || g === '.nogroup.') params.set('group', g);
                                                                    else params.delete('group');
                                                                    return params;
                                                                });
                                                            }}
                                                            options={[
                                                                {label: 'All', value: ''},
                                                                {label: 'No Group', value: '.nogroup.'},
                                                                ...getUniqueGroups().map(group => ({
                                                                    label: group.name,
                                                                    value: group.key,
                                                                })),
                                                            ]}
                                                            disabled={getUniqueGroups().length === 0}
                                                        />
                                                    </div>

                                                    <div className="w-full lg:w-[150px]">
                                                        <CustomDropdown
                                                            label="Device Status"
                                                            value={selectedStatus || ''}
                                                            onChange={s => {
                                                                setSearchParams(params => {
                                                                    if (s) params.set('status', s);
                                                                    else params.delete('status');
                                                                    return params;
                                                                });
                                                            }}
                                                            options={statusOptions}
                                                        />
                                                    </div>

                                                    <div className="w-full lg:w-[180px]">
                                                        <CustomDropdown
                                                            label="Device Model"
                                                            value={selectedModel ?? ''}
                                                            onChange={m => {
                                                                setSearchParams(params => {
                                                                    if (m) params.set('model', m);
                                                                    else params.delete('model');
                                                                    return params;
                                                                });
                                                            }}
                                                            options={[
                                                                {label: 'All', value: ''},
                                                                ...getUniqueModels().map(opt => ({
                                                                    label: opt.model,
                                                                    value: opt.key,
                                                                })),
                                                            ]}
                                                            disabled={getUniqueModels().length === 0}
                                                        />
                                                    </div>

                                                    <div className="w-full lg:w-[170px]">
                                                        <CustomDropdown
                                                            label="Player Version"
                                                            value={selectedPlayerVersion ?? ''}
                                                            onChange={v => {
                                                                setSearchParams(params => {
                                                                    if (v) params.set('pver', v);
                                                                    else params.delete('pver');
                                                                    return params;
                                                                });
                                                            }}
                                                            options={[
                                                                {label: 'All', value: ''},
                                                                ...getUniquePlayerVersions().map(opt => ({
                                                                    label: opt.version,
                                                                    value: opt.key,
                                                                })),
                                                            ]}
                                                            disabled={getUniquePlayerVersions().length === 0}
                                                        />
                                                    </div>

                                                    {/* Wide on purpose: board labels are ~75 chars
                                                        ("eros-p1 · sun50iw9p1 (cupid) · 2 GB / 16 GB · kernel 4.9.170 · Android 10 (49)"). */}
                                                    <div className="w-full lg:w-[600px]">
                                                        <CustomDropdown
                                                            label="Hardware"
                                                            value={selectedBoard ?? ''}
                                                            onChange={v => {
                                                                setSearchParams(params => {
                                                                    if (v) params.set('board', v);
                                                                    else params.delete('board');
                                                                    // A board pick supersedes any exact-hash deep link.
                                                                    params.delete('hw');
                                                                    return params;
                                                                });
                                                            }}
                                                            options={[
                                                                {label: 'All', value: ''},
                                                                ...getHwFilterOptions().map(opt => ({
                                                                    label: opt.label,
                                                                    value: opt.key,
                                                                })),
                                                            ]}
                                                            disabled={getHwFilterOptions().length === 0}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Device List Table */}
                                        <div
                                            className="card-chrome p-0 overflow-hidden">
                                            <div
                                                className="border-b border-white/[0.06] bg-surface-inset px-4 py-3 flex flex-col lg:flex-row lg:items-center gap-3 lg:gap-4">
                                                {/* Left — Select All */}
                                                <div className="flex items-center gap-3">
                                                    <label
                                                        className="inline-flex items-center cursor-pointer group">
                                                        <input
                                                            type="checkbox"
                                                            checked={selectAll}
                                                            onChange={toggleSelectAll}
                                                            className="sr-only"
                                                        />
                                                        <div
                                                            className={`w-5 h-5 flex items-center justify-center border rounded transition-all duration-200 ${
                                                                selectAll
                                                                    ? 'bg-brand border-brand'
                                                                    : 'bg-white/[0.04] border-white/[0.12]'
                                                            } group-hover:shadow-md`}
                                                        >
                                                            {selectAll && (
                                                                <span
                                                                    className="text-white text-sm font-bold leading-none">+</span>
                                                            )}
                                                        </div>
                                                        <span
                                                            className="ml-2 text-sm text-gray-300">Select All</span>
                                                    </label>
                                                    <span className="text-sm text-gray-500 font-medium">
                                                        {selectedDevices.length} of {filteredDevices.length} selected
                                                        {filteredDevices.length !== Object.keys(devices).length && (
                                                            <span className="text-gray-600"> · only devices shown by the current filter</span>
                                                        )}
                                                    </span>
                                                </div>

                                                {/* Middle — Add to group (visually pinned to the
                                                    left side of the toolbar, separated from the
                                                    destructive actions by a wide gap). Only shown
                                                    when at least one device is selected. */}
                                                {selectedDevices.length > 0 && (
                                                    <div className="flex flex-col md:flex-row items-stretch md:items-center gap-2">
                                                        <span className="text-xs uppercase tracking-wider text-gray-400 whitespace-nowrap">
                                                            Add device to group
                                                        </span>
                                                        <div className="min-w-[220px]">
                                                            <CustomDropdown
                                                                label=""
                                                                value={groupInput}
                                                                onChange={setGroupInput}
                                                                placeholder="Select group…"
                                                                options={[
                                                                    {label: 'No Group', value: '.nogroup.'},
                                                                    ...getUniqueGroups().map(group => ({
                                                                        label: group.name,
                                                                        value: group.key,
                                                                    }))
                                                                ]}
                                                                disabled={getUniqueGroups().length === 0}
                                                            />
                                                        </div>
                                                        <button
                                                            disabled={getUniqueGroups().length === 0 || !groupInput}
                                                            onClick={assignGroupToSelected}
                                                            className="inline-flex items-center justify-center gap-2 h-10 px-4 rounded-lg text-sm font-medium bg-brand/15 border border-brand/40 text-white hover:bg-brand/25 transition-colors disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap"
                                                        >
                                                            OK
                                                        </button>
                                                    </div>
                                                )}

                                                {/* Right — system / destructive actions, pushed to
                                                    the far right via ml-auto so there's clear
                                                    horizontal separation from the group cluster. */}
                                                {selectedDevices.length > 0 && (
                                                    <div className="flex flex-wrap items-center gap-2 lg:gap-3 lg:ml-auto">
                                                        <button
                                                            onClick={() => setIsSendCommandModalOpen(true)}
                                                            className="inline-flex items-center justify-center gap-2 h-10 px-4 rounded-lg text-sm font-medium bg-brand/15 border border-brand/40 text-white hover:bg-brand/25 transition-colors"
                                                        >
                                                            Send Command
                                                        </button>

                                                        <button
                                                            onClick={() => bulkSetOtaEnabled(true)}
                                                            className="btn-secondary h-10"
                                                        >
                                                            OTA On
                                                        </button>
                                                        <button
                                                            onClick={() => bulkSetOtaEnabled(false)}
                                                            className="btn-secondary h-10"
                                                        >
                                                            OTA Off
                                                        </button>

                                                        <button
                                                            onClick={deleteSelectedDevices}
                                                            className="inline-flex items-center justify-center gap-2 h-10 px-4 rounded-lg text-sm font-medium bg-red-500/15 border border-red-500/40 text-red-300 hover:bg-red-500/25 transition-colors"
                                                        >
                                                            Delete
                                                        </button>
                                                    </div>
                                                )}
                                            </div>

                                            {/* Table Controls: Column toggles + Per Page selector.
                                                Column toggles sit in a single horizontally-scrolling row (scroll-brand)
                                                instead of wrapping into a tall stack - keeps the controls compact on
                                                phones. On lg+ they share the row with the per-page selector. */}
                                            <div className="px-4 py-2 border-b border-white/[0.06] flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 lg:gap-4 bg-surface-inset">
                                                <div className="flex items-center gap-3 flex-nowrap overflow-x-auto scroll-brand w-full lg:flex-1 min-w-0 pb-1">
                                                    {/* Master Toggle: show/hide all columns */}
                                                    {(() => {
                                                        const allOn = showMacAddress && showIpAddress && showLastSeen && showFeedback && showOta && showPlayerVersion && showSystem && showMsVer && showModel && showCountry && showHardware && showKiosk;
                                                        const setAll = (v) => {
                                                            setShowMacAddress(v);
                                                            setShowIpAddress(v);
                                                            setShowLastSeen(v);
                                                            setShowFeedback(v);
                                                            setShowOta(v);
                                                            setShowPlayerVersion(v);
                                                            setShowSystem(v);
                                                            setShowMsVer(v);
                                                            setShowModel(v);
                                                            setShowCountry(v);
                                                            setShowHardware(v);
                                                            setShowKiosk(v);
                                                        };
                                                        return (
                                                            <>
                                                                <label className="inline-flex items-center cursor-pointer gap-1.5 shrink-0">
                                                                    <span className="text-sm font-semibold text-gray-200">All</span>
                                                                    <div
                                                                        onClick={() => setAll(!allOn)}
                                                                        className={`relative w-10 h-5 rounded-full transition-colors duration-200 ${
                                                                            allOn ? 'bg-brand' : 'bg-white/[0.1]'
                                                                        }`}
                                                                    >
                                                                        <div
                                                                            className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform duration-200 ${
                                                                                allOn ? 'translate-x-5' : 'translate-x-0'
                                                                            }`}
                                                                        />
                                                                    </div>
                                                                </label>
                                                                <span className="h-5 w-px bg-gray-600 mx-1 shrink-0" />
                                                            </>
                                                        );
                                                    })()}
                                                    {/* MAC Address Toggle */}
                                                    <label className="inline-flex items-center cursor-pointer gap-1.5 shrink-0">
                                                        <span className="text-sm text-gray-400">MAC</span>
                                                        <div
                                                            onClick={() => setShowMacAddress(!showMacAddress)}
                                                            className={`relative w-10 h-5 rounded-full transition-colors duration-200 ${
                                                                showMacAddress ? 'bg-brand' : 'bg-white/[0.1]'
                                                            }`}
                                                        >
                                                            <div
                                                                className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform duration-200 ${
                                                                    showMacAddress ? 'translate-x-5' : 'translate-x-0'
                                                                }`}
                                                            />
                                                        </div>
                                                    </label>
                                                    {/* IP Address Toggle */}
                                                    <label className="inline-flex items-center cursor-pointer gap-1.5 shrink-0">
                                                        <span className="text-sm text-gray-400">IP</span>
                                                        <div
                                                            onClick={() => setShowIpAddress(!showIpAddress)}
                                                            className={`relative w-10 h-5 rounded-full transition-colors duration-200 ${
                                                                showIpAddress ? 'bg-brand' : 'bg-white/[0.1]'
                                                            }`}
                                                        >
                                                            <div
                                                                className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform duration-200 ${
                                                                    showIpAddress ? 'translate-x-5' : 'translate-x-0'
                                                                }`}
                                                            />
                                                        </div>
                                                    </label>
                                                    {/* Last Seen Toggle */}
                                                    <label className="inline-flex items-center cursor-pointer gap-1.5 shrink-0">
                                                        <span className="text-sm text-gray-400">Last Seen</span>
                                                        <div
                                                            onClick={() => setShowLastSeen(!showLastSeen)}
                                                            className={`relative w-10 h-5 rounded-full transition-colors duration-200 ${
                                                                showLastSeen ? 'bg-brand' : 'bg-white/[0.1]'
                                                            }`}
                                                        >
                                                            <div
                                                                className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform duration-200 ${
                                                                    showLastSeen ? 'translate-x-5' : 'translate-x-0'
                                                                }`}
                                                            />
                                                        </div>
                                                    </label>
                                                    {/* Feedback Toggle */}
                                                    <label className="inline-flex items-center cursor-pointer gap-1.5 shrink-0">
                                                        <span className="text-sm text-gray-400">Feedback</span>
                                                        <div
                                                            onClick={() => setShowFeedback(!showFeedback)}
                                                            className={`relative w-10 h-5 rounded-full transition-colors duration-200 ${
                                                                showFeedback ? 'bg-brand' : 'bg-white/[0.1]'
                                                            }`}
                                                        >
                                                            <div
                                                                className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform duration-200 ${
                                                                    showFeedback ? 'translate-x-5' : 'translate-x-0'
                                                                }`}
                                                            />
                                                        </div>
                                                    </label>
                                                    {/* OTA Toggle */}
                                                    <label className="inline-flex items-center cursor-pointer gap-1.5 shrink-0">
                                                        <span className="text-sm text-gray-400">OTA</span>
                                                        <div
                                                            onClick={() => setShowOta(!showOta)}
                                                            className={`relative w-10 h-5 rounded-full transition-colors duration-200 ${
                                                                showOta ? 'bg-brand' : 'bg-white/[0.1]'
                                                            }`}
                                                        >
                                                            <div
                                                                className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform duration-200 ${
                                                                    showOta ? 'translate-x-5' : 'translate-x-0'
                                                                }`}
                                                            />
                                                        </div>
                                                    </label>
                                                    {/* Player Version Toggle */}
                                                    <label className="inline-flex items-center cursor-pointer gap-1.5 shrink-0">
                                                        <span className="text-sm text-gray-400">Player Ver</span>
                                                        <div
                                                            onClick={() => setShowPlayerVersion(!showPlayerVersion)}
                                                            className={`relative w-10 h-5 rounded-full transition-colors duration-200 ${
                                                                showPlayerVersion ? 'bg-brand' : 'bg-white/[0.1]'
                                                            }`}
                                                        >
                                                            <div
                                                                className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform duration-200 ${
                                                                    showPlayerVersion ? 'translate-x-5' : 'translate-x-0'
                                                                }`}
                                                            />
                                                        </div>
                                                    </label>
                                                    {/* System Toggle */}
                                                    <label className="inline-flex items-center cursor-pointer gap-1.5 shrink-0">
                                                        <span className="text-sm text-gray-400">System</span>
                                                        <div
                                                            onClick={() => setShowSystem(!showSystem)}
                                                            className={`relative w-10 h-5 rounded-full transition-colors duration-200 ${
                                                                showSystem ? 'bg-brand' : 'bg-white/[0.1]'
                                                            }`}
                                                        >
                                                            <div
                                                                className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform duration-200 ${
                                                                    showSystem ? 'translate-x-5' : 'translate-x-0'
                                                                }`}
                                                            />
                                                        </div>
                                                    </label>
                                                    {/* HW (hardware group hash) Toggle */}
                                                    <label className="inline-flex items-center cursor-pointer gap-1.5 shrink-0">
                                                        <span className="text-sm text-gray-400">HW</span>
                                                        <div
                                                            onClick={() => setShowHardware(!showHardware)}
                                                            className={`relative w-10 h-5 rounded-full transition-colors duration-200 ${
                                                                showHardware ? 'bg-brand' : 'bg-white/[0.1]'
                                                            }`}
                                                        >
                                                            <div
                                                                className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform duration-200 ${
                                                                    showHardware ? 'translate-x-5' : 'translate-x-0'
                                                                }`}
                                                            />
                                                        </div>
                                                    </label>
                                                    {/* MS Ver Toggle */}
                                                    <label className="inline-flex items-center cursor-pointer gap-1.5 shrink-0">
                                                        <span className="text-sm text-gray-400">MS Ver</span>
                                                        <div
                                                            onClick={() => setShowMsVer(!showMsVer)}
                                                            className={`relative w-10 h-5 rounded-full transition-colors duration-200 ${
                                                                showMsVer ? 'bg-brand' : 'bg-white/[0.1]'
                                                            }`}
                                                        >
                                                            <div
                                                                className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform duration-200 ${
                                                                    showMsVer ? 'translate-x-5' : 'translate-x-0'
                                                                }`}
                                                            />
                                                        </div>
                                                    </label>
                                                    {/* Model Toggle */}
                                                    <label className="inline-flex items-center cursor-pointer gap-1.5 shrink-0">
                                                        <span className="text-sm text-gray-400">Model</span>
                                                        <div
                                                            onClick={() => setShowModel(!showModel)}
                                                            className={`relative w-10 h-5 rounded-full transition-colors duration-200 ${
                                                                showModel ? 'bg-brand' : 'bg-white/[0.1]'
                                                            }`}
                                                        >
                                                            <div
                                                                className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform duration-200 ${
                                                                    showModel ? 'translate-x-5' : 'translate-x-0'
                                                                }`}
                                                            />
                                                        </div>
                                                    </label>
                                                    {/* Country Toggle */}
                                                    <label className="inline-flex items-center cursor-pointer gap-1.5 shrink-0">
                                                        <span className="text-sm text-gray-400">Country</span>
                                                        <div
                                                            onClick={() => setShowCountry(!showCountry)}
                                                            className={`relative w-10 h-5 rounded-full transition-colors duration-200 ${
                                                                showCountry ? 'bg-brand' : 'bg-white/[0.1]'
                                                            }`}
                                                        >
                                                            <div
                                                                className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform duration-200 ${
                                                                    showCountry ? 'translate-x-5' : 'translate-x-0'
                                                                }`}
                                                            />
                                                        </div>
                                                    </label>
                                                    {/* Kiosk Toggle */}
                                                    <label className="inline-flex items-center cursor-pointer gap-1.5 shrink-0">
                                                        <span className="text-sm text-gray-400">Kiosk</span>
                                                        <div
                                                            onClick={() => setShowKiosk(!showKiosk)}
                                                            className={`relative w-10 h-5 rounded-full transition-colors duration-200 ${
                                                                showKiosk ? 'bg-brand' : 'bg-white/[0.1]'
                                                            }`}
                                                        >
                                                            <div
                                                                className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform duration-200 ${
                                                                    showKiosk ? 'translate-x-5' : 'translate-x-0'
                                                                }`}
                                                            />
                                                        </div>
                                                    </label>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <span className="text-xs text-gray-400">Show</span>
                                                    <select
                                                        value={devicesPerPage}
                                                        onChange={(e) => {
                                                            setDevicesPerPage(Number(e.target.value));
                                                            setCurrentPage(1); // Reset to first page when changing per-page
                                                        }}
                                                        className="bg-surface-inset border border-white/[0.08] text-white text-sm rounded-md px-2 py-1 focus:outline-none focus:ring-1 focus:ring-brand/40"
                                                    >
                                                        <option value={10}>10</option>
                                                        <option value={25}>25</option>
                                                        <option value={50}>50</option>
                                                        <option value={100}>100</option>
                                                    </select>
                                                    <span className="text-xs text-gray-400">per page</span>
                                                    <span className="text-xs text-gray-500 ml-2">
                                                        ({sortedDevices.length} total)
                                                    </span>
                                                </div>
                                            </div>

                                            <div className="w-full overflow-x-auto scroll-brand">
                                                <table className="w-full min-w-[980px] table-fixed text-white">
                                                    <thead>
                                                    <tr className="bg-surface-inset text-xs uppercase tracking-wider text-gray-400">
                                                        <th className="w-14 py-2 px-2 text-left text-sm font-semibold">{/*Select + Status*/}</th>
                                                        {showMacAddress && (
                                                            <th className="w-28 py-2 px-4 text-left text-sm font-semibold">MAC</th>
                                                        )}
                                                        <th className="w-24 py-2 px-4 text-left text-sm font-semibold">Device Name</th>
                                                        <th className="w-28 py-2 px-4 text-left text-sm font-semibold">Customer</th>
                                                        <th className="w-24 py-2 px-4 text-left text-sm font-semibold">First Name</th>
                                                        <th className="w-24 py-2 px-4 text-left text-sm font-semibold">Last Name</th>
                                                        {showIpAddress && (
                                                            <th className="w-24 py-2 px-4 text-left text-sm font-semibold">IP Address</th>
                                                        )}
                                                        {showLastSeen && (
                                                            <th className="w-28 py-2 px-4 text-left text-sm font-semibold">Last Seen</th>
                                                        )}
                                                        {showCountry && (
                                                            <th className="w-20 py-2 px-4 text-left text-sm font-semibold">Country</th>
                                                        )}
                                                        {showKiosk && (
                                                            <th className="w-24 py-2 px-4 text-center text-sm font-semibold align-middle"
                                                                title="Kiosk mode - the switch asks (kiosk_mode), the label shows what the device actually applied (kiosk_state)">Kiosk</th>
                                                        )}
                                                        <th className="w-28 py-2 px-2 text-left text-sm font-semibold">Group</th>
                                                        {showModel && (
                                                            <th className="w-24 py-2 px-4 text-left text-sm font-semibold">Model</th>
                                                        )}
                                                        {showSystem && (
                                                            <th className="w-24 py-2 px-4 text-center text-sm font-semibold align-middle">System</th>
                                                        )}
                                                        {showHardware && (
                                                            <th className="w-24 py-2 px-4 text-center text-sm font-semibold align-middle" title="Hardware group (hw_hash) - equal hashes share a board">HW</th>
                                                        )}
                                                        {showMsVer && (
                                                            <th className="w-16 py-2 px-4 text-center text-sm font-semibold align-middle">MS Ver</th>
                                                        )}
                                                        <th className="w-20 py-2 px-2 text-center text-sm font-semibold align-middle">Updates</th>
                                                        {showFeedback && (
                                                            <th className="w-16 py-2 px-2 text-center text-sm font-semibold align-middle">Feedback</th>
                                                        )}
                                                        {showOta && (
                                                            <th className="w-12 py-2 px-4 text-center text-sm font-semibold align-middle">OTA</th>
                                                        )}
                                                        <th className="w-12 py-2 px-2 text-center text-sm font-semibold align-middle">Player</th>
                                                        <th className="w-10 py-2 text-sm font-semibold align-right">{/*Actions*/}</th>
                                                    </tr>
                                                    </thead>
                                                    <tbody>
                                                    {currentDevices.map(({key: deviceId, ...info}) => (
                                                        <tr
                                                            key={deviceId}
                                                            className="border-t border-white/[0.04] hover:bg-white/[0.02] transition-colors cursor-pointer"
                                                            onClick={() => navigate(`/devices/${macToSlug(deviceId)}`)}
                                                        >
                                                            <td className="py-3 px-2">
                                                                <div className="flex items-center gap-2">
                                                                    <label
                                                                        className="inline-flex items-center group cursor-pointer"
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            toggleDeviceSelection(deviceId)
                                                                        }}>
                                                                        <input
                                                                            type="checkbox"
                                                                            checked={selectedDevices.includes(deviceId)}
                                                                            onChange={() => toggleDeviceSelection(deviceId)}
                                                                            className="sr-only"
                                                                        />
                                                                        <div
                                                                            className={`w-4 h-4 flex items-center justify-center border rounded transition-all duration-200 ${selectedDevices.includes(deviceId)
                                                                                ? 'bg-brand border-brand'
                                                                                : 'bg-white/[0.04] border-white/[0.12]'} group-hover:shadow-sm`}
                                                                        >
                                                                            {selectedDevices.includes(deviceId) && (
                                                                                <span
                                                                                    className="text-white text-[12px] font-bold leading-none">+</span>
                                                                            )}
                                                                        </div>
                                                                    </label>
                                                                    <span
                                                                        className={`inline-block w-3 h-3 rounded-full ${
                                                                            info.status === 'online'
                                                                                ? 'bg-green-500 shadow-[0_0_6px_rgba(34,197,94,0.6)]'
                                                                                : 'bg-red-500 shadow-[0_0_6px_rgba(239,68,68,0.6)]'
                                                                        }`}
                                                                        title={info.status === 'online' ? 'Online' : 'Offline'}
                                                                    />
                                                                </div>
                                                            </td>
                                                            {showMacAddress && (
                                                                <td className="py-0.5 px-4 text-xs text-gray-400 font-mono">{deviceId}</td>
                                                            )}
                                                            {/* Device Name: primary = player-sourced name. manual_device_name is the
                                                                operator backup label - shown small below when player data exists, or
                                                                promoted to primary when the player hasn't reported a name yet
                                                                (e.g. before provisioning). */}
                                                            <td className="py-0.5 px-4">
                                                                <div className="text-sm text-gray-300">{info.name || info.manual_device_name || '-'}</div>
                                                                {info.name && info.manual_device_name && (
                                                                    <div className="text-xs text-gray-500 truncate" title="Manual label (backup)">
                                                                        {info.manual_device_name}
                                                                    </div>
                                                                )}
                                                            </td>
                                                            {/* Customer: primary = player-sourced business_name; manual_customer_name is
                                                                the operator backup, promoted to primary when the player hasn't reported one. */}
                                                            <td className="py-0.5 px-4">
                                                                <div className="text-sm text-brand-400 font-semibold">{info.business_name || info.manual_customer_name || '-'}</div>
                                                                {info.business_name && info.manual_customer_name && (
                                                                    <div className="text-xs text-gray-500 truncate" title="Manual label (backup)">
                                                                        {info.manual_customer_name}
                                                                    </div>
                                                                )}
                                                            </td>
                                                            {/* First / Last name - player-sourced, default-visible columns. */}
                                                            <td className="py-0.5 px-4 text-sm text-gray-300">{info.first_name || '-'}</td>
                                                            <td className="py-0.5 px-4 text-sm text-gray-300">{info.last_name || '-'}</td>
                                                            {showIpAddress && (
                                                                <td className="py-0.5 px-4 text-sm text-gray-300">{info.ip_address || 'N/A'}</td>
                                                            )}
                                                            {showLastSeen && (
                                                                <td className="py-1 px-3 text-xs text-gray-400 italic font-medium whitespace-nowrap">
                                                                    {formatTimestampHumanely(info.last_time_seen)}
                                                                </td>
                                                            )}
                                                            {showCountry && (
                                                                <td className="py-1 px-3 text-xs text-gray-300 align-middle font-medium whitespace-nowrap">
                                                                    {countryOfIp(ipToCountry, info.ip_address) ? (
                                                                        <div
                                                                            className="inline-flex items-center gap-2">
                                                                            <img
                                                                                src={`https://flagcdn.com/24x18/${countryOfIp(ipToCountry, info.ip_address).country_code?.toLowerCase()}.png`}
                                                                                alt="Country Flag"
                                                                                className="w-3 h-3 object-cover"
                                                                                onError={(e) => {
                                                                                    e.currentTarget.style.display = 'none';
                                                                                    e.currentTarget.nextElementSibling.textContent = 'Unknown';
                                                                                    e.currentTarget.nextElementSibling.classList.add('text-gray-400', 'italic', 'font-medium');
                                                                                }}
                                                                            />
                                                                            <span>{countryOfIp(ipToCountry, info.ip_address).country_name || 'Unknown'}</span>
                                                                        </div>
                                                                    ) : (
                                                                        <span
                                                                            className="text-gray-400 italic font-medium">Unknown</span>
                                                                    )}
                                                                </td>
                                                            )}
                                                            {showKiosk && (
                                                                <td className="py-0.5 px-4 text-center align-middle"
                                                                    onClick={e => e.stopPropagation()}>
                                                                    <KioskCell mac={deviceId} device={info} />
                                                                </td>
                                                            )}

                                                            <td className="py-0.5 px-4 text-sm text-gray-300"
                                                                onClick={e => e.stopPropagation()}>
                                                                {info.group && groupList[info.group]?.name ? (
                                                                    <span
                                                                        className="brand-pill inline-block px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap">
                                                    {groupList[info.group].name}
                                                </span>
                                                                ) : (
                                                                    <span
                                                                        className="px-2 text-xs text-gray-400 italic font-medium whitespace-nowrap">No Group</span>
                                                                )}
                                                            </td>
                                                            {showModel && (
                                                                <td className="py-0.5 px-4 text-sm text-gray-300">{info.device_model || '-'}</td>
                                                            )}
                                                            {showSystem && (
                                                                <td className="py-0.5 px-4 text-sm text-gray-300 text-center align-middle">{info.system_version || '-'}</td>
                                                            )}
                                                            {showHardware && (
                                                                <td className="py-0.5 px-4 text-sm text-center align-middle">
                                                                    {getHwHash(info) ? (
                                                                        <span
                                                                            className="font-mono text-gray-300"
                                                                            title={[info.hw_fingerprint?.platform, info.hw_fingerprint?.hardware, info.hw_fingerprint?.origin_build].filter(Boolean).join(' / ')}
                                                                        >
                                                                            {getHwHash(info)}
                                                                        </span>
                                                                    ) : (
                                                                        <span className="text-gray-600" title="No fingerprint reported (Monitoring < 6.0.4)">-</span>
                                                                    )}
                                                                </td>
                                                            )}
                                                            {showMsVer && (
                                                                <td className="py-0.5 px-4 text-sm text-gray-300 text-center align-middle">{info.monitoring_service_version || '-'}</td>
                                                            )}
                                                            <td className="py-0.5 px-2 text-center align-middle">
                                                                <div className="flex items-center justify-center gap-1" title={(() => {
                                                                    const issues = [];
                                                                    const pv = getDeviceAppVersion(info, PLAYER_PACKAGE);
                                                                    const ua = findDeviceApp(info, UPDATER_PACKAGES);
                                                                    const ma = findDeviceApp(info, MONITORING_PACKAGES);
                                                                    if (!pv || needsAppUpdate(pv, updateStats.player.latestVersion)) issues.push('Player');
                                                                    if (!ua || needsAppUpdate(ua.version, getLatestAppVersion(ua.packageName))) issues.push('Updater');
                                                                    if (!ma || needsAppUpdate(ma.version, getLatestAppVersion(ma.packageName))) issues.push('Monitoring');
                                                                    if (needsOtaUpdate(info)) issues.push('OTA');
                                                                    return issues.length > 0 ? `Needs: ${issues.join(', ')}` : 'All up to date';
                                                                })()}>
                                                                    {/* Player indicator */}
                                                                    <span className={`w-2 h-2 rounded-full ${
                                                                        (() => {
                                                                            const v = getDeviceAppVersion(info, PLAYER_PACKAGE);
                                                                            if (!v) return 'bg-gray-500';
                                                                            return needsAppUpdate(v, updateStats.player.latestVersion) ? 'bg-orange-500' : 'bg-green-500';
                                                                        })()
                                                                    }`} />
                                                                    {/* Updater indicator */}
                                                                    <span className={`w-2 h-2 rounded-full ${
                                                                        (() => {
                                                                            const a = findDeviceApp(info, UPDATER_PACKAGES);
                                                                            if (!a) return 'bg-gray-500';
                                                                            return needsAppUpdate(a.version, getLatestAppVersion(a.packageName)) ? 'bg-orange-500' : 'bg-green-500';
                                                                        })()
                                                                    }`} />
                                                                    {/* Monitoring indicator */}
                                                                    <span className={`w-2 h-2 rounded-full ${
                                                                        (() => {
                                                                            const a = findDeviceApp(info, MONITORING_PACKAGES);
                                                                            if (!a) return 'bg-gray-500';
                                                                            return needsAppUpdate(a.version, getLatestAppVersion(a.packageName)) ? 'bg-orange-500' : 'bg-green-500';
                                                                        })()
                                                                    }`} />
                                                                    {/* OTA indicator */}
                                                                    <span className={`w-2 h-2 rounded-full ${
                                                                        needsOtaUpdate(info) ? 'bg-orange-500' : 'bg-green-500'
                                                                    }`} />
                                                                </div>
                                                            </td>
                                                            {showFeedback && (
                                                                <td className="py-0.5 px-2 text-center align-middle">
                                                                    {/* Command Feedback indicator */}
                                                                    {info.feedback ? (
                                                                        <span
                                                                            className={`px-2 py-0.5 rounded text-xs font-medium ${
                                                                                info.feedback === 'success'
                                                                                    ? 'bg-green-500/20 text-green-400'
                                                                                    : info.feedback === 'failed'
                                                                                        ? 'bg-red-500/20 text-red-400'
                                                                                        : info.feedback === 'pending'
                                                                                            ? 'bg-yellow-500/20 text-yellow-400'
                                                                                            : 'bg-gray-500/20 text-gray-400'
                                                                            }`}
                                                                            title={`Last command feedback: ${info.feedback}`}
                                                                        >
                                                                            {info.feedback === 'success' ? '✓' : info.feedback === 'failed' ? '✗' : info.feedback === 'pending' ? '...' : '-'}
                                                                        </span>
                                                                    ) : (
                                                                        <span className="text-gray-500">-</span>
                                                                    )}
                                                                </td>
                                                            )}
                                                            {showOta && (
                                                                <td className="py-0.5 px-4 text-center align-middle" onClick={e => e.stopPropagation()}>
                                                                    <button
                                                                        onClick={() => toggleDeviceOtaEnabled(deviceId)}
                                                                        className={`px-2 py-0.5 rounded text-xs font-medium transition-all ${
                                                                            info.ota_enabled === false
                                                                                ? 'bg-red-500/20 text-red-400 hover:bg-red-500/30'
                                                                                : 'bg-green-500/20 text-green-400 hover:bg-green-500/30'
                                                                        }`}
                                                                    >
                                                                        {info.ota_enabled === false ? 'OFF' : 'ON'}
                                                                    </button>
                                                                </td>
                                                            )}
                                                            <td className="py-0.5 px-2 text-center align-middle">
                                                                {/* Player Status indicator */}
                                                                <FiPlay
                                                                    className={`w-4 h-4 inline-block ${
                                                                        info.player_status === 'running'
                                                                            ? 'text-green-400'
                                                                            : info.player_status === 'stopped'
                                                                                ? 'text-red-400'
                                                                                : info.player_status === 'restarted'
                                                                                    ? 'text-yellow-400'
                                                                                    : 'text-gray-500'
                                                                    }`}
                                                                    title={
                                                                        info.player_status === 'running'
                                                                            ? 'Player Running'
                                                                            : info.player_status === 'stopped'
                                                                                ? 'Player Stopped'
                                                                                : info.player_status === 'restarted'
                                                                                    ? 'Player Restarted'
                                                                                    : 'Player Not Installed'
                                                                    }
                                                                />
                                                                {showPlayerVersion && (() => {
                                                                    const appList = info?.app_list || [];
                                                                    const playerApp = Array.isArray(appList)
                                                                        ? appList.find(a => a?.packageName === 'rs.musicdj.player')
                                                                        : null;
                                                                    return playerApp?.appVersion
                                                                        ? <span className="text-xs text-gray-400 ml-1">v{playerApp.appVersion}</span>
                                                                        : null;
                                                                })()}
                                                            </td>
                                                            <td className="py-0.5 text-sm align-right">
                                                                <div className="flex items-center gap-1.5">
                                                                    <button
                                                                        className="p-1.5 rounded-md bg-purple-500/15 text-purple-300 hover:bg-purple-500/25 transition-colors"
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            navigate(`/remote?device=${encodeURIComponent(deviceId)}`);
                                                                        }}
                                                                        title="Remote Control"
                                                                    >
                                                                        <FiEye className="w-4 h-4"/>
                                                                    </button>
                                                                    <button
                                                                        className="p-1.5 rounded-md bg-brand/15 text-brand-400 hover:bg-brand/25 transition-colors"
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            setSelectedDevices([deviceId]);
                                                                            setIsSendCommandModalOpen(true);
                                                                        }}
                                                                        title="Send Command"
                                                                    >
                                                                        <FiSend className="w-4 h-4"/>
                                                                    </button>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                        {totalPages > 1 && (
                                            <div className="flex justify-center mt-6">
                                                <nav className="flex items-center gap-2">
                                                    <button
                                                        onClick={() => paginate(currentPage - 1)}
                                                        disabled={currentPage === 1}
                                                        className="p-2 rounded-md text-gray-400 hover:text-white hover:bg-white/[0.06] disabled:opacity-40 transition-colors"
                                                    >
                                                        <FiChevronLeft/>
                                                    </button>
                                                    {Array.from({length: totalPages}, (_, i) => i + 1).map((page) => (
                                                        <button
                                                            key={page}
                                                            onClick={() => paginate(page)}
                                                            className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                                                                currentPage === page ? 'brand-pill' : 'text-gray-400 hover:text-white hover:bg-white/[0.06]'
                                                            }`}
                                                        >
                                                            {page}
                                                        </button>
                                                    ))}
                                                    <button
                                                        onClick={() => paginate(currentPage + 1)}
                                                        disabled={currentPage === totalPages}
                                                        className="p-2 rounded-md text-gray-400 hover:text-white hover:bg-white/[0.06] disabled:opacity-40 transition-colors"
                                                    >
                                                        <FiChevronRight/>
                                                    </button>
                                                </nav>
                                            </div>
                                        )}
                                    </div>
        </div>
    );
}

function CommandsTab({deviceId, devices, sendCommand, commandRegistry}) {
const [commandParams, setCommandParams] = useState({});
const [openCategories, setOpenCategories] = useState({});

                                const toggleCategory = (category) => {
                                setOpenCategories((prev) => ({
                                ...prev,
                                [category]: !prev[category],
                            }));
                            };

                                const handleCommandParamChange = (command, param, value) => {
                                setCommandParams((prev) => ({
                                ...prev,
                                [command]: {...prev[command], [param]: value},
                            }));
                            };

                                useEffect(() => {
                                const initialCategories = {};
                                Object.keys(commandRegistry).forEach((category) => {
                                initialCategories[category] = false;
                            });
                                setOpenCategories(initialCategories);
                            }, []);

                                return (
                                <div className="space-y-6">
                            {Object.entries(commandRegistry).map(([category, {description, commands}]) => {
                                const groupedCommands = {};
                                commands.forEach((cmd) => {
                                if (cmd.toggle && cmd.displayName) {
                                if (!groupedCommands[cmd.displayName]) {
                                groupedCommands[cmd.displayName] = [];
                            }
                                groupedCommands[cmd.displayName].push(cmd);
                            } else {
                                groupedCommands[cmd.command] = [cmd];
                            }
                            });

                                return (
                                <div key={category} className="card-inset p-6">
                                <button
                                    onClick={() => toggleCategory(category)}
                                    className="w-full flex justify-between items-center text-left"
                                >
                                    <div>
                                        <h4 className="text-lg font-semibold text-brand-400">{category}</h4>
                                        <p className="text-sm text-gray-400">{description}</p>
                                    </div>
                                    {openCategories[category] ? (
                                        <FiChevronUp className="text-gray-400"/>
                                    ) : (
                                        <FiChevronDown className="text-gray-400"/>
                                    )}
                                </button>
                                {openCategories[category] && (
                                    <div className="mt-4 space-y-6">
                                        {Object.entries(groupedCommands).map(([groupKey, cmdGroup], index) => {
                                            if (cmdGroup.length === 1 && !cmdGroup[0].toggle) {
                                                const cmd = cmdGroup[0];
                                                return (
                                                    <div key={index} className="border-t border-gray-600 pt-4">
                                                        <div className="flex items-center justify-between mb-2">
                                                            <div>
                                                                <span
                                                                    className="text-gray-200 font-medium">{cmd.command}</span>
                                                                <p className="text-sm text-gray-400">{cmd.description}</p>
                                                            </div>
                                                            <button
                                                                onClick={() => sendCommand(deviceId, cmd, setCommandParams, commandParams)}
                                                                className="btn-primary"
                                                            >
                                                                Execute
                                                            </button>
                                                        </div>
                                                        {Object.keys(cmd.params).length > 0 && (
                                                            <div className="mt-2 space-y-3">
                                                                {cmd.params.level !== undefined && (
                                                                    <input
                                                                        type="number"
                                                                        placeholder={`Level (e.g., ${cmd.params.level})`}
                                                                        className="input-field"
                                                                        onChange={(e) =>
                                                                            handleCommandParamChange(cmd.command, 'level', parseInt(e.target.value))
                                                                        }
                                                                    />
                                                                )}
                                                                {cmd.params.url && (
                                                                    <input
                                                                        type="text"
                                                                        placeholder={`URL (e.g., ${cmd.params.url})`}
                                                                        className="input-field"
                                                                        onChange={(e) =>
                                                                            handleCommandParamChange(cmd.command, 'url', e.target.value)
                                                                        }
                                                                    />
                                                                )}
                                                                {cmd.params.text && (
                                                                    <input
                                                                        type="text"
                                                                        placeholder={`Text (e.g., ${cmd.params.text})`}
                                                                        className="input-field"
                                                                        onChange={(e) =>
                                                                            handleCommandParamChange(cmd.command, 'text', e.target.value)
                                                                        }
                                                                    />
                                                                )}
                                                                {cmd.params.package && (
                                                                    <input
                                                                        type="text"
                                                                        placeholder={`Package (e.g., ${cmd.params.package})`}
                                                                        className="input-field"
                                                                        onChange={(e) =>
                                                                            handleCommandParamChange(cmd.command, 'package', e.target.value)
                                                                        }
                                                                    />
                                                                )}
                                                                {cmd.params.intent && (
                                                                    <input
                                                                        type="text"
                                                                        placeholder={`Intent (e.g., ${cmd.params.intent})`}
                                                                        className="input-field"
                                                                        onChange={(e) =>
                                                                            handleCommandParamChange(cmd.command, 'intent', e.target.value)
                                                                        }
                                                                    />
                                                                )}
                                                                {cmd.params.data && (
                                                                    <input
                                                                        type="text"
                                                                        placeholder={`Data (e.g., ${cmd.params.data})`}
                                                                        className="input-field"
                                                                        onChange={(e) =>
                                                                            handleCommandParamChange(cmd.command, 'data', e.target.value)
                                                                        }
                                                                    />
                                                                )}
                                                                {cmd.params.enabled !== undefined && (
                                                                    <CustomDropdown
                                                                        value={cmd.params?.enabled ? 'true' : 'false'}
                                                                        onChange={(val) => handleCommandParamChange(cmd.command, 'enabled', val === 'true')}
                                                                        options={[
                                                                            { label: 'Enable', value: 'true' },
                                                                            { label: 'Disable', value: 'false' }
                                                                        ]}
                                                                    />
                                                                )}
                                                                {cmd.params.x !== undefined && (
                                                                    <input
                                                                        type="number"
                                                                        placeholder={`X (e.g., ${cmd.params.x})`}
                                                                        className="input-field"
                                                                        onChange={(e) =>
                                                                            handleCommandParamChange(cmd.command, 'x', parseInt(e.target.value))
                                                                        }
                                                                    />
                                                                )}
                                                                {cmd.params.y !== undefined && (
                                                                    <input
                                                                        type="number"
                                                                        placeholder={`Y (e.g., ${cmd.params.y})`}
                                                                        className="input-field"
                                                                        onChange={(e) =>
                                                                            handleCommandParamChange(cmd.command, 'y', parseInt(e.target.value))
                                                                        }
                                                                    />
                                                                )}
                                                                {cmd.params.x1 !== undefined && (
                                                                    <input
                                                                        type="number"
                                                                        placeholder={`X1 (e.g., ${cmd.params.x1})`}
                                                                        className="input-field"
                                                                        onChange={(e) =>
                                                                            handleCommandParamChange(cmd.command, 'x1', parseInt(e.target.value))
                                                                        }
                                                                    />
                                                                )}
                                                                {cmd.params.y1 !== undefined && (
                                                                    <input
                                                                        type="number"
                                                                        placeholder={`Y1 (e.g., ${cmd.params.y1})`}
                                                                        className="input-field"
                                                                        onChange={(e) =>
                                                                            handleCommandParamChange(cmd.command, 'y1', parseInt(e.target.value))
                                                                        }
                                                                    />
                                                                )}
                                                                {cmd.params.x2 !== undefined && (
                                                                    <input
                                                                        type="number"
                                                                        placeholder={`X2 (e.g., ${cmd.params.x2})`}
                                                                        className="input-field"
                                                                        onChange={(e) =>
                                                                            handleCommandParamChange(cmd.command, 'x2', parseInt(e.target.value))
                                                                        }
                                                                    />
                                                                )}
                                                                {cmd.params.y2 !== undefined && (
                                                                    <input
                                                                        type="number"
                                                                        placeholder={`Y2 (e.g., ${cmd.params.y2})`}
                                                                        className="input-field"
                                                                        onChange={(e) =>
                                                                            handleCommandParamChange(cmd.command, 'y2', parseInt(e.target.value))
                                                                        }
                                                                    />
                                                                )}
                                                                {cmd.params.duration !== undefined && (
                                                                    <input
                                                                        type="number"
                                                                        placeholder={`Duration (e.g., ${cmd.params.duration}ms)`}
                                                                        className="input-field"
                                                                        onChange={(e) =>
                                                                            handleCommandParamChange(
                                                                                cmd.command,
                                                                                'duration',
                                                                                parseInt(e.target.value)
                                                                            )
                                                                        }
                                                                    />
                                                                )}
                                                                {cmd.params.seconds !== undefined && (
                                                                    <input
                                                                        type="number"
                                                                        placeholder={`Seconds (e.g., ${cmd.params.seconds})`}
                                                                        className="input-field"
                                                                        onChange={(e) =>
                                                                            handleCommandParamChange(
                                                                                cmd.command,
                                                                                'seconds',
                                                                                parseInt(e.target.value)
                                                                            )
                                                                        }
                                                                    />
                                                                )}
                                                                {cmd.params.keycode !== undefined && (
                                                                    <input
                                                                        type="number"
                                                                        placeholder={`Keycode (e.g., ${cmd.params.keycode})`}
                                                                        className="input-field"
                                                                        onChange={(e) =>
                                                                            handleCommandParamChange(
                                                                                cmd.command,
                                                                                'keycode',
                                                                                parseInt(e.target.value)
                                                                            )
                                                                        }
                                                                    />
                                                                )}
                                                                {cmd.params.message && (
                                                                    <input
                                                                        type="text"
                                                                        placeholder={`Message (e.g., ${cmd.params.message})`}
                                                                        className="input-field"
                                                                        onChange={(e) =>
                                                                            handleCommandParamChange(cmd.command, 'message', e.target.value)
                                                                        }
                                                                    />
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            }

                                            if (cmdGroup.length === 2 && cmdGroup[0].toggle) {
                                                const deviceState = devices[deviceId];
                                                const stateValue = deviceState ? deviceState[cmdGroup[0].toggle.stateField] : false;
                                                const activeCmd = stateValue
                                                    ? cmdGroup.find((cmd) => !cmd.toggle.enabledValue)
                                                    : cmdGroup.find((cmd) => cmd.toggle.enabledValue);

                                                return (
                                                    <div key={index} className="border-t border-gray-600 pt-4">
                                                        <div className="flex items-center justify-between mb-2">
                                                            <div>
                                                        <span
                                                            className="text-gray-200 font-medium">{activeCmd.displayName}</span>
                                                                <p className="text-sm text-gray-400">{activeCmd.description}</p>
                                                            </div>
                                                            <label
                                                                className="relative inline-flex items-center cursor-pointer">
                                                                <input
                                                                    type="checkbox"
                                                                    checked={stateValue}
                                                                    onChange={() => sendCommand(deviceId, activeCmd, setCommandParams, commandParams)}
                                                                    className="sr-only peer"
                                                                />
                                                                <div
                                                                    className="w-11 h-6 bg-white/[0.1] rounded-full peer peer-checked:bg-brand transition-all"></div>
                                                                <div
                                                                    className="absolute left-1 top-1 w-4 h-4 bg-white rounded-full transition-all peer-checked:left-6"></div>
                                                            </label>
                                                        </div>
                                                    </div>
                                                );
                                            }

                                            return null;
                                        })}
                                    </div>
                                )}
                            </div>
                            );
                            })}
                        </div>
                    );
                    }

/**
 * Kiosk cell for the device list.
 *
 * Two different facts live here and they are deliberately not merged: `kiosk_mode` is what the
 * dashboard ASKED for, `kiosk_state` is what the device actually applied ("on" / "off" / "failed").
 * They disagree while a command is in flight, while a technician has unlocked the device with the
 * service PIN, and permanently if the device is offline - so the switch reflects the request and the
 * label underneath reports reality. Showing only one of them is how an operator ends up believing a
 * box is locked when it is not.
 *
 * kiosk_mode is always written as an explicit true/false and never removed: the agent only reacts to
 * a field that exists, so turning kiosk off means writing false.
 */
function KioskCell({ mac, device }) {
    const [busy, setBusy] = useState(false);
    const requested = device?.kiosk_mode === true;
    const applied = device?.kiosk_state; // "on" | "off" | "failed" | undefined (older agent)

    const toggle = async () => {
        if (busy) return;
        const next = !requested;
        setBusy(true);
        try {
            await update(ref(db, `mdm/devices/${mac}`), { kiosk_mode: next });
            toast.success(next ? 'Kiosk ON sent' : 'Kiosk OFF sent');
        } catch (err) {
            toast.error(`Kiosk change failed: ${err.message}`);
        } finally {
            setBusy(false);
        }
    };

    const labelTone = applied === 'on' ? 'text-green-400'
        : applied === 'failed' ? 'text-red-400'
            : applied === 'off' ? 'text-gray-400'
                : 'text-gray-500 italic';

    return (
        <div className="inline-flex flex-col items-center gap-1">
            <div
                onClick={toggle}
                title={`Requested: ${requested ? 'ON' : 'OFF'} — device reports: ${applied ?? 'unknown'}`}
                className={`relative w-10 h-5 rounded-full transition-colors duration-200 ${busy ? 'opacity-50' : 'cursor-pointer'} ${
                    requested ? 'bg-brand' : 'bg-white/[0.1]'
                }`}
            >
                <div
                    className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform duration-200 ${
                        requested ? 'translate-x-5' : 'translate-x-0'
                    }`}
                />
            </div>
            <span className={`text-[10px] font-semibold uppercase tracking-wide ${labelTone}`}>
                {applied === undefined ? '—' : applied}
            </span>
        </div>
    );
}
